
import {Component, Vue, Watch} from "vue-property-decorator";
import {mapState} from "vuex";

// Translation
import {TRANSLATIONS_FETCH} from "@/store/ActionTypes";

// Repositories
import RecipesRepository from "@/utils/API/RecipesRepository";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import IngredientsRepository from "@/utils/API/IngredientsRepository";
import AllergenRepository from "@/utils/API/AllergenRepository";

//Utils
import {
  debounce,
  desanitizeString,
  getErrorMessage,
  getPreviewNutrientsForCountry,
  mapNutritions,
  restrictCharactersByRegex,
  round
} from "@/utils/Helpers";

//Models
import {RecipeCalculate, RecipeCalculateIngredient, RecipeCalculateSubrecipe} from "@/models/Recipes/RecipeCalculate";
import {NutrientSource} from "@/models/NutrientSource/NutrientSource";
import {IngredientMeasurementEnum} from "@/models/Ingredient/IngredientMeasurementEnum";
import {IngredientReduced} from "@/models/Search/IngredientReduced";
import {RecipeReduced} from "@/models/Search/RecipeReduced";
import {SearchResults} from "@/models/Search/SearchResults";
import {SearchIngredients} from "@/models/Ingredient/SearchIngredients";
import {createDefaultIngredient, Ingredient} from "@/models/Ingredient/Ingredient";
import {CreateRecipe} from "@/models/Recipes/CreateRecipe";
import {UpdateRecipe} from "@/models/Recipes/UpdateRecipe";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import { getAllergenStatusMeta } from "@/utils/allergenStatus";
import {RecipeIngredient} from "@/models/Recipes/RecipeIngredient";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import type {Subrecipe} from '@/models/Recipes/Subrecipe';
import {ServingSizeMeasurement} from "@/models/Measurement/ServingSizeMeasurement";

// Components
import {VueEditor} from "vue2-editor";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vSelect from "@/components/vSelect.vue";
import vSelectFx from "@/components/vSelectFx.vue";
import vNutritionLabel from "@/components/vNutritionLabel.vue";
import vModal from "@/components/vModal.vue";
import vPreLoader from "@/components/vPreLoader.vue";
import vIconWithTooltip from "@/components/vIconWithTooltip.vue";
import vIngredientManagement from "@/components/vIngredientManagement.vue";
import vExportLabel from "@/components/vExportLabel.vue";
import vTabbedContent from "@/components/vTabbedContent.vue";
import {FoodGroupClassification} from "@/models/Recipes/FoodGroupClassification";
import {Recipe} from "@/models/Recipes/Recipe";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import vAdjustOverrideNutrientsForm from "@/components/vAdjustOverrideNutrientsForm.vue";
import {applyNutrientOverrides} from "@/utils/API/RecipeNutrientOverrideRepository";
import {NutrientOverridesParams} from "@/models/Nutrient/NutrientOverridesParams";
import {CalculationMethodModalData} from "@/models/Recipes/CalculationMethodModalData";
import VCalculationMethodModal from "@/components/vCalculationMethodModal.vue";
import {RecipeServingType} from "@/models/Recipes/RecipeServingType";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import {LossType} from "@/models/Recipes/LossType";
import {DailyValueCalculationMethod} from "@/models/Recipes/DailyValueCalculationMethod";
import VRecipePrintout from "@/components/vRecipePrintout.vue";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import {NutritionCountry} from "@/models/NutritionLabels/NutritionCountry";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import VUnroundedValuesWarning from "@/components/vUnroundedValuesWarning.vue";
import {createDefaultProductType, RecipeProductType} from "@/models/Recipes/RecipeProductType";
import {RecipeMetadata} from "@/models/Recipes/RecipeMetadata";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import VFoPWarning from "@/components/vFoPWarning.vue";
import VClaimsAndStatementsModal from "@/components/vClaimsAndStatementsModal.vue";
import VEnabledClaimsList from "@/components/vEnabledClaimsList.vue";
import ClaimsRepository from "@/utils/API/ClaimsRepository";
import {RoundingRules} from "@/models/NutritionLabels/RoundingRules";
import VPreviewLabel from "@/components/vPreviewLabel.vue";
import VBanner from "@/components/vBanner.vue";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import VAllergenStatusIcon from "@/components/allergens/VAllergenStatusIcon.vue";
import {AccountState} from "@/store/modules/AccountModule";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";
import Toggle from "@/components/bootstrap/Toggle.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {SavedAllergen} from "@/models/Allergen/SelectedAllergen";
import {AllergenConfigurationTargetType} from "@/models/Allergen/AllergenConfigurationData";
import {MlAnalysisResponse} from "@/models/AllergenML/AllergenML";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";
import RestaurantRepository from "@/utils/API/RestaurantRepository";
import { TrialRestaurantActivity } from "@/models/Restaurants/TrialRestaurantActivity";
import vTrialLabelCongratulationsModal from "@/components/vTrialLabelCongratulationsModal.vue";
import {
  buildCreateRestaurantIngredientPayload,
  buildUpdateRestaurantIngredientPayload,
  getCreatedIngredientId
} from "@/utils/ingredientPayloads";

let _ = require("lodash");
let lodashDebounce = require("lodash.debounce");

//Types
type SelectObj = { id: number; name: string };
type FoodGroupSubClassification = {
  id: number;
  class: string;
  subClass: string;
  relatedFoodGroupClassifications: [];
};
type ServingWeighMeasurement = {
  id: string;
  name: string;
};
type Category = { id: number; name: string; recipes?: any };
type RecipeCopyMoistureLoss = {
  ingredients: any[];
  childRecipes: any[];
  cookingMethodId: number;
  lossAmount: number;
  lossType: number;
  recipeId: number;
  weight: number;
};

//Interfaces
interface RecipesManagementState {
  recipe: Recipe;
  addCategory: SelectObj;
  loadingRecipe: boolean;
}

@Component({
  methods: {
    restrictCharactersByRegex,
    debounce,
    round,
    desanitizeString
  },
  components: {
    VAllergenStatusIcon,
    vTrialLabelCongratulationsModal,
    VUpsellModal,
    VBanner,
    VPreviewLabel,
    VFoPWarning,
    VUnroundedValuesWarning,
    VRecipePrintout,
    VCalculationMethodModal,
    vBreadcrumbs,
    vSelect,
    vSelectFx,
    VueEditor,
    vNutritionLabel,
    vModal,
    vPreLoader,
    vIconWithTooltip,
    vIngredientManagement,
    vExportLabel,
    vTabbedContent,
    vAdjustOverrideNutrientsForm,
    VClaimsAndStatementsModal,
    VEnabledClaimsList,
    Toggle,
    AllergensConfiguration
  },
  computed: {
    CanadaFOPHighInMetadata() {
      return CanadaFOPHighInMetadata;
    },
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  }
})
class RecipesManagement extends Vue {
  /** VARIABLES **/
  NutrientType = NutrientType;
  DailyValueCalculationMethod = DailyValueCalculationMethod;

  // Flags
  canadaFlag = require("@/assets/img/customer-facing/recipe-edit/icon-canada-flag.png");
  usaFlag = require("@/assets/img/customer-facing/recipe-edit/icon-usa-flag.png");
  customIngrFlag = require("@/assets/img/customer-facing/recipe-edit/icon-custom.png");
  customRecipeFlag = require("@/assets/img/customer-facing/recipe-edit/icon-custom-recipe.png");
  ukFlag = require("@/assets/img/customer-facing/recipe-edit/icon-uk-flag.png");
  sugarAlcoholsFlag = require("@/assets/img/customer-facing/recipe-edit/sugar-alcohol-database-icon.png");

  // Recipe state
  recipesManagementState: RecipesManagementState = {
    recipe: new Recipe(),
    addCategory: { id: 0, name: "" },
    loadingRecipe: false,
  };

  selectedOption: string = "Ingredient name";
  searchOptions: { name: string; value: string }[] = [
    { name: "Ingredient name", value: "Ingredient name" },
    { name: "Food code", value: "Food code" },
  ];

  selectedRoundingRule: RoundingRules = { id: 0, name: 'FDA' };

  recipeMetadata: RecipeMetadata = {
    recipeId: 0,
    countryId: 0,
    referenceAmount: 0,
    productTypeId: 0,
    roundingRuleIndex: 0,
    isSupplementLabel: false,
    showAllergenOnPrintout: false,
  };

  isUpdatingRecipeCountryMetadata = false;
  pendingRoundingRuleUpdate: RoundingRules | null = null;
  localizationState: LocalizationState;
  accountState: AccountState;
  categories: Category[] = [];
  foodProductTypes: RecipeProductType[] = [];

  // Booleans for modals
  showCategoryModal = false;
  showRecipeInst = false;
  showIngredientModal = false;
  showScaleRecipeModal = false;
  showMoistureLossModal = false;
  showServingSizeModal = false;
  showAddIngredientModal = false;
  showExportLabelModal = false;
  showTrialUserModal = false;
  showLabelOverrideModal = false;
  isTransparentBackground = false;
  showClaimModal = false;
  selectedAllergens: SavedAllergen[] = [];
  allergenContainsText: string = "";
  mayContainText: Partial<Record<LabelLanguage, string[]>> = {};
  recipeAllergenLanguage: keyof typeof LabelLanguage = "English";
  showExportLabelDemoModal = false;
  isExportLabelDemoLoading = false;
  showIncludeAllergenConfigurationModal = false;
  includeAllergenConfigurationModalLead = "";
  includeAllergenConfigurationModalMessage = "";
  skipAllergenReloadOnReset = false;
  private resolveIncludeAllergenConfigurationModal: ((value: boolean) => void) | null = null;

  exportLabelDemoSrc = "https://app.supademo.com/embed/cmn3doqd369pqsjdoewdkiato?embed_v=2&utm_source=embed";

  enabledClaims: string[] = [];

  // Search ingredient modal variables
  searchResults: SearchResults = {
    customIngredients: [],
    ingredients: [],
    recipes: [],
    dbIngredients: [],
  };
  numberOfSearchClicks = 0; // number of how many times search ingredient button is clicked (needed for version control)
  previousSearchTerm = ""; // keep track of previously searched term
  includeCustomIngredients = true; // for include custom ingredient checkbox
  ingredientSearchString = ""; // binding of the user input for searching ingredients
  hasAutoCorrection = false; //autocorrect flag for misspelled word
  autoCorrection = ""; // auto correct frase
  autoCorrectionHtml = ""; // html content containing autocorrected phrase
  searchLoading = false; // show preloader while searching
  noResult = false; // show message if there's no result
  activeIngredientSearchMeasurementDropdownKey: string | null = null;

  // Add new ingredient modal variables
  newCustomIngredient: Ingredient = createDefaultIngredient();
  addCustomIngredientModalTitle = ""; // modal title when adding custom ingredient, title can also be 'edit'
  postSaveCustomIngredientAiApplyToken = 0;

  // Scale recipe variables
  scaledRecipeServingSize: number | null = null; // the serving size that the user enters in the 'scale' modal, divide the current serving size by this number to get coefficient for scaling

  // Moisture loss variables
  moistureLossValue = 0;

  recipeWeightLossSelectedMeasurement: SelectObj = { id: 0, name: "g" };
  moistureLossMeasurementOptions: SelectObj[] = [
    { id: 0, name: "g" },
    { id: 1, name: "Percent" },
  ];
  moistureLossSelectedMeasurement: SelectObj = this.moistureLossMeasurementOptions[0];

  invalidMoistureLossValue = false;
  selectedCookingMethod: SelectObj = { id: -1, name: "Not specified" };
  cookingMethods: SelectObj[] = [];
  foodGroupClasses: SelectObj[] = [];
  foodGroupClassesMatrix: {
    class: string;
    subClass: FoodGroupSubClassification[]
  }[] = [];
  recipeCopy: RecipeCopyMoistureLoss = {
    ingredients: [],
    childRecipes: [],
    cookingMethodId: 0,
    lossAmount: 0,
    lossType: 0,
    recipeId: 0,
    weight: 0,
  };

  // Serving size modal variables
  allIngredientsHaveLiquidVolumes: Boolean = true;
  selectedServingWeightMeasurement: ServingWeighMeasurement = {
    id: "g",
    name: "g",
  };
  servingCount = 1;
  servingQuantityInUnitOfMeasurement = 1;
  recipeServingType = 0;

  // General reference variables
  subrecipeServingSizes: RecipeCompositionMeasurement[] = [];
  subrecipeServingMeasurements: ServingSizeMeasurement[] = [];

  nutrientCountryNames: string[] = [
    "Canada",
    "SR Legacy",
    "UK",
    "Branded",
    "Foundation",
  ];
  nutrientSources: NutrientSource[] = []; // Nutrient source countries
  nutrientsListForSearch: number[] = []; // Ids of all selected checkboxes in add ingredient
  isDisplayFop: boolean = true;
  recipeSaved: boolean = true; // Track if the user saved the recipe after making changes so that buttons get enabled
  calculationMethodModal: CalculationMethodModalData = new CalculationMethodModalData();
  LabelType = LabelTypeMetadata;
  nutritionCountries: NutritionCountry[] = [];
  printRecipeMeta: PrintRecipeDetails = {
    ingredients: [],
    brandingLogo: null,
    statementsAndClaims: []
  };
  isSupplementLabelLoaded: boolean = false;

  nutritionRules = [
    { id: 0, name: 'FDA' },
    { id: 1, name: 'FSIS (2025)' },
    { id: 2, name: 'FSIS (2007)' },
  ];

  showTrialUpsellModal: boolean = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  showTrialExportLabelConfirmationModal = false;
  isConfirmingTrialExportLabel = false;
  showTrialLabelCongratulations = false;
  hasHandledOpenExportLabelQuery = false;

  closeTrialLabelCongratulations() {
    this.showTrialLabelCongratulations = false;
    this.showExportLabelModal = false;
  }

  provide() {
    return {
      bopFetchPreview: () => {},
    };
  }

  onAllergenValueUpdated(value: SavedAllergen[]) {
    this.selectedAllergens = [...value];
  }

  async beforeOpenRecipeAiDetector() {
    if (this.$route.name !== "recipesEdit") {
      return true;
    }

    let servingCount = null;
    let servingQuantityInUnitOfMeasurement = null;

    if (this.recipeServingType == 0) {
      servingCount = Math.round(this.servingCount * 100) / 100;
    }
    else {
      servingQuantityInUnitOfMeasurement =
        Math.round(
          this.recipesManagementState.recipe
            .servingQuantityInUnitOfMeasurement * 100
        ) / 100;
    }

    const productType = this.recipesManagementState.recipe.recipeProductType
      ? this.recipesManagementState.recipe.recipeProductType
      : createDefaultProductType();

    const updateRecipe: UpdateRecipe = {
      recipes: this.recipesManagementState.recipe.childRecipes.map((child: any) => ({
        recipeId: child.recipe.id,
        recipeCompositionMeasurementId: child.recipeCompositionMeasurementId,
        quantity: child.quantity ? child.quantity : 1,
        isAddedSugar: child.isAddedSugar,
      })),
      ingredients: this.recipesManagementState.recipe.ingredients.map((ingr: any) => ({
        quantity: ingr.quantity,
        ingredientMeasurementId: ingr.measurement.id,
        isAddedSugar: ingr.isAddedSugar,
      })),
      description: this.recipesManagementState.recipe.description,
      recipeCategoryId: this.recipesManagementState.recipe.recipeCategory
        ? this.recipesManagementState.recipe.recipeCategory.id
        : 0,
      name: this.recipesManagementState.recipe.name,
      recipeId: this.recipesManagementState.recipe.id,
      instructions: this.recipesManagementState.recipe.instructions,
      recipeServingType:
        this.recipeServingType !=
        this.recipesManagementState.recipe.recipeServingType
          ? this.recipeServingType
          : this.recipesManagementState.recipe.recipeServingType,
      servingCount,
      servingQuantityInUnitOfMeasurement,
      servingUnitOfMeasurement:
        this.recipesManagementState.recipe.servingUnitOfMeasurement,
      adjustedAddedSugarsValue:
        this.recipesManagementState.recipe.adjustedAddedSugarsValue,
      nutritionCountry: this.countryId,
      referenceAmount: this.recipesManagementState.recipe.referenceAmount,
      productTypeId: productType.id,
      nutrientTypesToCalculate: this.recipesManagementState.recipe.nutritionCountry
        ? this.recipeMetadata.isSupplementLabel ? null : getPreviewNutrientsForCountry(
          this.recipesManagementState.recipe.nutritionCountry.id)
        : null,
    };

    try {
      const response = await RecipesRepository.updateRecipe(updateRecipe);

      await this.saveAllergenConfiguration(updateRecipe.recipeId);
      this.skipAllergenReloadOnReset = false;
      this.recipeSaved = true;
      this.mapRecipeDetails(response.data.result);
      await this.ensureRecipeNutritionCountryLoaded(response.data.result.nutritionCountry);
      await this.hydrateRecipeStatusesFromSavedAnalysis(response.data.result.id);
      this.loadRecipeComponents();

      return true;
    }
    catch (error: any) {
      this.skipAllergenReloadOnReset = false;
      const message = error?.response
        ? error.response.data.errorMessages[0].userVisibleMessage
        : this.translations.get("network_error");
      this.$snotify.error(message);
      return false;
    }
  }

  async onRecipeAiAnalysisApplied(result: MlAnalysisResponse) {
    const recipe = this.recipesManagementState?.recipe;

    if (!recipe) {
      return;
    }

    this.applyRecipeAnalysisStatuses(result);

    await this.refreshRecipeAllergenStatuses(recipe.id);
  }

  onMayContainTextUpdated(value: Partial<Record<LabelLanguage, string[]>>) {
    this.mayContainText = { ...value };
  }

  analysisResultHasDetectedAllergen(result: MlAnalysisResponse) {
    if ((result.ingredients || []).length > 0) {
      return result.ingredients.some((ingredientResult) =>
        this.analysisEntryHasDetectedAllergen(ingredientResult.allergens, ingredientResult.overrideMarkers || [])
      );
    }

    return this.analysisEntryHasDetectedAllergen(result.allergens || [], []);
  }

  analysisEntryHasDetectedAllergen(
    allergens: Array<{ id: string }> = [],
    overrideMarkers: Array<{ allergenCode: string; isAllergen: boolean }> = []
  ) {
    const detectedCodes = new Set(
      allergens
        .map((allergen) => (allergen.id || "").trim().toLowerCase())
        .filter(Boolean)
    );

    for (const marker of overrideMarkers) {
      const code = (marker.allergenCode || "").trim().toLowerCase();

      if (!code) {
        continue;
      }

      if (marker.isAllergen) {
        detectedCodes.add(code);
      }
      else {
        detectedCodes.delete(code);
      }
    }

    return detectedCodes.size > 0;
  }

  applyRecipeAnalysisStatuses(result: MlAnalysisResponse) {
    const recipe = this.recipesManagementState?.recipe;

    if (!recipe) {
      return;
    }

    recipe.hasAllergenAnalysis = true;
    recipe.hasAllergen = this.analysisResultHasDetectedAllergen(result);

    const subrecipeStatuses = new Map<number, { hasAllergenAnalysis: boolean; hasAllergen: boolean }>(
      (result.subrecipes || [])
        .filter((subrecipe) => Boolean(subrecipe.recipeId))
        .map((subrecipe) => [
          subrecipe.recipeId,
          {
            hasAllergenAnalysis: Boolean(subrecipe.hasAllergenAnalysis),
            hasAllergen: Boolean(subrecipe.hasAllergen),
          }
        ])
    );

    if (subrecipeStatuses.size > 0) {
      for (const childRecipe of recipe.childRecipes || []) {
        const childRecipeId = childRecipe?.recipe?.id;

        if (!childRecipeId || !subrecipeStatuses.has(childRecipeId)) {
          continue;
        }

        const subrecipeStatus = subrecipeStatuses.get(childRecipeId)!;
        this.$set(childRecipe.recipe, "hasAllergenAnalysis", subrecipeStatus.hasAllergenAnalysis);
        this.$set(childRecipe.recipe, "hasAllergen", subrecipeStatus.hasAllergen);
      }
    }

    const ingredientAnalysisById = new Map<number, boolean>();
    const childRecipeAnalysisById = new Map<number, boolean>();
    const childRecipeAnalysisByName = new Map<string, boolean>();

    for (const ingredientResult of result.ingredients || []) {
      const hasDetectedAllergen = this.analysisEntryHasDetectedAllergen(
        ingredientResult.allergens,
        ingredientResult.overrideMarkers || []
      );
      const subrecipeId = this.extractSubrecipeIdFromLineage(ingredientResult.lineagePath);
      const subrecipeName = this.extractSubrecipeNameFromLineage(ingredientResult.lineagePath);

      if (subrecipeId) {
        if (!childRecipeAnalysisById.has(subrecipeId)) {
          childRecipeAnalysisById.set(subrecipeId, false);
        }

        childRecipeAnalysisById.set(subrecipeId, childRecipeAnalysisById.get(subrecipeId) || hasDetectedAllergen);
        continue;
      }

      if (subrecipeName) {
        if (!childRecipeAnalysisByName.has(subrecipeName)) {
          childRecipeAnalysisByName.set(subrecipeName, false);
        }

        childRecipeAnalysisByName.set(
          subrecipeName,
          childRecipeAnalysisByName.get(subrecipeName) || hasDetectedAllergen
        );
        continue;
      }

      if (!ingredientResult.ingredientId) {
        continue;
      }

      if (!ingredientAnalysisById.has(ingredientResult.ingredientId)) {
        ingredientAnalysisById.set(ingredientResult.ingredientId, false);
      }

      ingredientAnalysisById.set(
        ingredientResult.ingredientId,
        ingredientAnalysisById.get(ingredientResult.ingredientId) || hasDetectedAllergen
      );
    }

    for (const recipeIngredient of recipe.ingredients || []) {
      const ingredientId = recipeIngredient?.ingredient?.id;

      if (!ingredientId || !ingredientAnalysisById.has(ingredientId)) {
        continue;
      }

      this.$set(recipeIngredient.ingredient, "hasAllergenAnalysis", true);
      this.$set(recipeIngredient.ingredient, "hasAllergen", ingredientAnalysisById.get(ingredientId) || false);
    }

    for (const childRecipe of recipe.childRecipes || []) {
      const childRecipeId = childRecipe?.recipe?.id;
      const childRecipeName = this.normalizeLineageSegment(childRecipe?.recipe?.name);
      const hasMatchedStatus = Boolean(
        (childRecipeId && childRecipeAnalysisById.has(childRecipeId))
          || (childRecipeName && childRecipeAnalysisByName.has(childRecipeName))
      );

      if (!hasMatchedStatus) {
        continue;
      }

      this.$set(childRecipe.recipe, "hasAllergenAnalysis", true);
      this.$set(childRecipe.recipe, "hasAllergen", Boolean(
        (childRecipeId ? childRecipeAnalysisById.get(childRecipeId) : false)
          || (childRecipeName ? childRecipeAnalysisByName.get(childRecipeName) : false)
      ));
    }
  }

  extractSubrecipeIdFromLineage(lineagePath: string | null | undefined) {
    const match = (lineagePath || "").match(/Subrecipe\/(\d+)/i);
    return match ? Number(match[1]) : 0;
  }

  extractSubrecipeNameFromLineage(lineagePath: string | null | undefined) {
    const matches = [...(lineagePath || "").matchAll(/Subrecipe\/([^>]+)/gi)];

    if (matches.length === 0) {
      return "";
    }

    return this.normalizeLineageSegment(matches[matches.length - 1]?.[1]);
  }

  normalizeLineageSegment(value: string | null | undefined) {
    return (value || "").trim().toLowerCase();
  }

  async refreshRecipeAllergenStatuses(recipeId: number) {
    if (!recipeId) {
      return;
    }

    try {
      const response = await RecipesRepository.getRecipeDetails(recipeId, true);

      if (!response?.data) {
        return;
      }

      this.mapRecipeDetails(response.data);
      await this.ensureRecipeNutritionCountryLoaded(response.data.nutritionCountry);
      await this.loadAllergenConfiguration(response.data.id);
      await this.hydrateRecipeStatusesFromSavedAnalysis(response.data.id);
    }
    catch {
      return;
    }
  }

  async hydrateRecipeStatusesFromSavedAnalysis(recipeId: number) {
    const restaurantId = this.accountState?.userDetails?.restaurantId;
    const countryId = this.countryId;

    if (!this.isAllergenAiAddonActive || !recipeId || !restaurantId || !countryId) {
      return;
    }

    try {
      const response = await AllergenMLRepository.getSavedRecipeAnalysis({
        restaurantId,
        recipeId,
        isCombinedRecipe: false,
        countryId,
        nutrientSource: countryId,
      });

      this.applyRecipeAnalysisStatuses(response.data);
    }
    catch {
      return;
    }
  }

  async saveAllergenConfiguration(recipeId: number) {
    await AllergenRepository.saveAllergenConfiguration({
      targetType: AllergenConfigurationTargetType.Recipe,
      targetId: recipeId,
      allergens: this.selectedAllergens || [],
      mayContainText: this.mayContainText || {}
    });
  }

  async saveIngredientAllergenConfiguration(
    ingredientId: number,
    allergens: SavedAllergen[] = [],
    mayContainText: Partial<Record<LabelLanguage, string[]>> = {}
  ) {
    await AllergenRepository.saveAllergenConfiguration({
      targetType: AllergenConfigurationTargetType.Ingredient,
      targetId: ingredientId,
      allergens,
      mayContainText
    });
  }

  async saveCurrentAllergenConfiguration() {
    const recipeId = this.recipesManagementState?.recipe?.id;

    if (!recipeId) {
      return;
    }

    await this.saveAllergenConfiguration(recipeId);
  }

  async loadAllergenConfiguration(recipeId: number) {
    try {
      const response = await AllergenRepository.getStoredAllergenConfiguration(
        AllergenConfigurationTargetType.Recipe,
        recipeId
      );

      this.selectedAllergens = response.data?.allergens || [];
      this.mayContainText = response.data?.mayContainText || {};
    }
    catch {
      this.selectedAllergens = [];
      this.mayContainText = {};
    }
  }

  cloneMayContainText(value: Partial<Record<LabelLanguage, string[]>>) {
    const cloned: Partial<Record<LabelLanguage, string[]>> = {};

    Object.keys(value || {}).forEach((key) => {
      const language = key as LabelLanguage;
      cloned[language] = [...(value[language] || [])];
    });

    return cloned;
  }

  mergeMayContainText(
    currentValue: Partial<Record<LabelLanguage, string[]>>,
    incomingValue: Partial<Record<LabelLanguage, string[]>>
  ) {
    const merged = this.cloneMayContainText(currentValue || {});

    Object.keys(incomingValue || {}).forEach((key) => {
      const language = key as LabelLanguage;
      const existingValues = new Set((merged[language] || []).map((value) => (value || "").trim().toLowerCase()));

      (incomingValue[language] || []).forEach((value) => {
        const trimmedValue = (value || "").trim();

        if (!trimmedValue) {
          return;
        }

        if (!existingValues.has(trimmedValue.toLowerCase())) {
          merged[language] = [...(merged[language] || []), trimmedValue];
          existingValues.add(trimmedValue.toLowerCase());
        }
      });
    });

    return merged;
  }

  mergeIngredientAllergenConfigurationIntoRecipe(configuration: {
    allergens?: SavedAllergen[];
    mayContainText?: Partial<Record<LabelLanguage, string[]>>;
  }) {
    const nextAllergensById = new Map<number, SavedAllergen>();

    (this.selectedAllergens || []).forEach((allergen: SavedAllergen) => {
      if (typeof allergen === "object" && allergen?.id) {
        nextAllergensById.set(allergen.id, allergen);
      }
    });

    (configuration.allergens || []).forEach((allergen: SavedAllergen) => {
      if (typeof allergen === "object" && allergen?.id && !nextAllergensById.has(allergen.id)) {
        nextAllergensById.set(allergen.id, allergen);
      }
    });

    this.selectedAllergens = Array.from(nextAllergensById.values());
    this.mayContainText = this.mergeMayContainText(
      this.mayContainText || {},
      configuration.mayContainText || {}
    );
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  closeTrialExportLabelConfirmationModal() {
    if (this.isConfirmingTrialExportLabel) {
      return;
    }

    this.showTrialExportLabelConfirmationModal = false;
  }

  openIncludeAllergenConfigurationModal(lead: string, message: string) {
    this.includeAllergenConfigurationModalLead = lead;
    this.includeAllergenConfigurationModalMessage = message;
    this.showIncludeAllergenConfigurationModal = true;

    return new Promise<boolean>((resolve) => {
      this.resolveIncludeAllergenConfigurationModal = resolve;
    });
  }

  confirmIncludeAllergenConfigurationModal() {
    this.showIncludeAllergenConfigurationModal = false;
    const resolve = this.resolveIncludeAllergenConfigurationModal;
    this.resolveIncludeAllergenConfigurationModal = null;
    resolve?.(true);
  }

  cancelIncludeAllergenConfigurationModal() {
    this.showIncludeAllergenConfigurationModal = false;
    const resolve = this.resolveIncludeAllergenConfigurationModal;
    this.resolveIncludeAllergenConfigurationModal = null;
    resolve?.(false);
  }

  async maybeOpenExportLabelFromQuery() {
    if (this.hasHandledOpenExportLabelQuery || this.$route.query.openExportLabel !== "1") {
      return;
    }

    if (!this.recipesManagementState.recipe.id) {
      return;
    }

    this.hasHandledOpenExportLabelQuery = true;
    await this.showExportLabel();
  }

  /**
   * Determines the default label type based on the recipe metadata properties.
   * Evaluates conditions such as whether the label corresponds to supplement facts,
   * FSIS requirements, or standard nutrition facts.
   *
   * @return {LabelTypeMetadata} The default label type, which can be SupplementFacts, FSIS, or NutritionFacts.
   */
  get defaultLabelType() {
    if (this.recipeMetadata.isSupplementLabel) {
      return LabelTypeMetadata.SupplementFacts;
    }

    if (this.recipeMetadata.roundingRuleIndex != 0 && this.recipeMetadata.countryId == NutritionLabelCountry.US) {
      return LabelTypeMetadata.FSIS;
    }

    return LabelTypeMetadata.NutritionFacts;
  }

  tryInitialSave() {
    if (
      !this.recipesManagementState.recipe.id
      && this.recipesManagementState.recipe.name
      && this.recipesManagementState.recipe.recipeCategory.id
    ) {
      this.submit('');
    }
  }

  /**
   * Retrieves the name of the country associated with the nutrition data.
   *
   * @return {string | null} The name of the country if available, or null if no country information exists.
   */
  get countryName(): string | null {
    if (this.recipesManagementState.recipe.nutritionCountry) {
      return this.recipesManagementState.recipe.nutritionCountry.name;
    }

    return null;
  }

  get exportLabelTitle(): string {
    const title = this.translations.get('rec_export_label') ?? '';
    const recipeName = this.recipesManagementState?.recipe?.name ?? '';

    return `${title} (${recipeName})`;
  }

  get isServingTypeWeight(): boolean {
    return this.recipeServingType === RecipeServingType.Weight;
  }

  /**
   * Retrieves the id of the country associated with the nutrition data.
   *
   * @return {number | null} The id of the country if available, or null if no country information exists.
   */
  get countryId(): number | null {
    if (this.recipesManagementState.recipe.nutritionCountry) {
      return this.recipesManagementState.recipe.nutritionCountry.id;
    }

    return null;
  }

  get containsAllergensText() {
    if (!this.recipeMetadata.showAllergenOnPrintout) {
      return "";
    }

    return this.allergenContainsText;
  }

  get isEmptyRecipe(){
    return this.recipesManagementState.recipe.ingredients.length == 0 && this.recipesManagementState.recipe.childRecipes.length == 0;
  }

  get hasIngredientSearchResults() {
    return (
      this.searchResults.ingredients.length > 0 ||
      this.searchResults.recipes.length > 0 ||
      this.searchResults.customIngredients.length > 0 ||
      this.searchResults.dbIngredients.length > 0
    );
  }

  get hasUsdaSearchGroup() {
    return this.shouldShowNutrientSource(NutrientSource.US);
  }

  get availableUsdaNutrientSources() {
    if (!this.hasUsdaSearchGroup) {
      return [];
    }

    return [
      NutrientSource.US,
      NutrientSource.Foundation,
      NutrientSource.Branded,
      NutrientSource.Fndds,
    ];
  }

  get availableSearchDatabases() {
    const sources = new Set<NutrientSource>();

    if (this.shouldShowNutrientSource(NutrientSource.Canada)) {
      sources.add(NutrientSource.Canada);
    }

    this.availableUsdaNutrientSources.forEach((source) => sources.add(source));

    if (this.shouldShowNutrientSource(NutrientSource.UK)) {
      sources.add(NutrientSource.UK);
    }

    if (this.showSugarAlcoholDatabase) {
      sources.add(NutrientSource.SugarAlcohols);
    }

    return Array.from(sources);
  }

  get allAvailableSearchDatabasesSelected() {
    return (
      this.includeCustomIngredients &&
      this.availableSearchDatabases.length > 0 &&
      this.availableSearchDatabases.every((source) =>
        this.nutrientsListForSearch.includes(source)
      )
    );
  }

  get allUsdaNutrientsSelected() {
    return (
      this.availableUsdaNutrientSources.length > 0 &&
      this.availableUsdaNutrientSources.every((source) =>
        this.nutrientsListForSearch.includes(source)
      )
    );
  }

  /** WATCHERS **/

  @Watch("caloriesCalculationMethod")
  onCaloriesCalculationMethodChange() {
    this.calculateNutritions();
  }

  /** METHODS **/
  get translations() {
    return this.localizationState.translations;
  }

  get allergenStatusLegendTooltipContent() {
    return [
      "<div class='allergens-popover-legend'>",
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge ai'><i class='fa fa-check'></i></span><span>${this.translations.get("allergens_ai_legend_ai") || "AI detected this allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge removed'><i class='fa fa-times'></i></span><span>${this.translations.get("allergens_ai_legend_removed") || "Manually marked as not an allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge user'><i class='fa fa-check'></i><span class='allergens-popover-badge-accent'><i class='fa fa-question'></i></span></span><span>${this.translations.get("allergens_ai_legend_user_added") || "Manually added as an allergen."}</span></div>`,
      "</div>"
    ].join("");
  }

  get showAllergenAiButton() {
    return this.accountState.userDetails.isTrial || this.isAllergenAiAddonActive;
  }

  get isAllergenAiAddonActive() {
    if (this.accountState.userDetails.isTrial) {
      return true;
    }

    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  get shouldShowRecipeAiDetector() {
    const recipe = this.recipesManagementState.recipe;
    return Boolean(recipe.id && !recipe.hasAllergenAnalysis);
  }

  getAllergenStatusVariant(item: Ingredient | Recipe) {
    return getAllergenStatusMeta(item).variant;
  }

  getAllergenStatusTooltip(item: Ingredient | Recipe) {
    return getAllergenStatusMeta(item).title;
  }

  goToCurrentRecipeAllergenAi() {
    const recipe = this.recipesManagementState.recipe;

    this.$router.push({
      name: "allergensAi",
      query: {
        tab: "recipes",
        id: String(recipe.id),
        name: recipe.name,
        analyzed: recipe.hasAllergenAnalysis ? "1" : "0",
      },
    });
  }

  goToIngredientAllergenAi(recipeIngredient: RecipeIngredient) {
    this.$router.push({
      name: "allergensAi",
      query: {
        tab: "customRecipes",
        id: String(recipeIngredient.ingredient.id),
        name: recipeIngredient.ingredient.name,
        analyzed: recipeIngredient.ingredient.hasAllergenAnalysis ? "1" : "0",
      },
    });
  }

  goToRecipeAllergenAi(subrecipe: Subrecipe) {
    this.$router.push({
      name: "allergensAi",
      query: {
        tab: "recipes",
        id: String(subrecipe.recipe.id),
        name: subrecipe.recipe.name,
        analyzed: subrecipe.recipe.hasAllergenAnalysis ? "1" : "0",
      },
    });
  }

  // Check if user has sugar alcohol addon
  get showSugarAlcoholDatabase() {
    if (this.accountState.userDetails.addOns.length > 0) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.SugarAlcohols);
    }
    return false;
  }

  get caloriesCalculationMethod() {
    return this.calculationMethodModal.selectedCalculationMethod;
  }

  get useRoundedValues() {
    return this.recipesManagementState.recipe.dailyValueCalculationMethod === DailyValueCalculationMethod.Rounded;
  }

  get yieldAdjustmentTabs() {
    // TODO
    let tabsArray = [
      { id: 1, name: this.translations.get("yield-adjustment_tab_moisture") },
      // {
      //   id: 2,
      //   name: this.translations.get("yield-adjustment_tab_weight-loss"),
      // },
    ];
    return tabsArray;
  }

  get productTypeName() {
    return this.recipesManagementState.recipe.recipeProductType
      ? this.recipesManagementState.recipe.recipeProductType.name
      : "";
  }

  async updatePrintMeta() {
    const response = await RecipesRepository.getPrintRecipeDetails(this.recipesManagementState.recipe.id);
    this.printRecipeMeta = response.data;
  }

  async printRecipe() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    await this.updatePrintMeta();
    await new Promise((res) => setTimeout(res, 500));
    window.print();
  }

  async showExportLabel() {
    if (this.accountState.userDetails.isTrial) {
      const trialExportLabelState = await this.checkTrialExportLabelAvailability();

      if (!trialExportLabelState.canAccess) {
        return;
      }

      if (trialExportLabelState.shouldConfirm) {
        this.showTrialExportLabelConfirmationModal = true;
        return;
      }
    }

    if (this.accountState.userDetails.nutritionLabelsLimit.labelsLimit == null) {
      await this.updatePrintMeta();
    }


    this.showExportLabelModal = true;
  }
  /**
   * Count total results
   */
  get totalResults() {
    if (!this.hasIngredientSearchResults) {
      return null;
    }

    if (this.selectedOption === "Ingredient name") {
      return (
        this.searchResults.ingredients.length +
        this.searchResults.recipes.length +
        this.searchResults.customIngredients.length
      );
    }
    else if (this.selectedOption === "Food code") {
      return this.searchResults.dbIngredients.length;
    }
  }
  /**
   * Remove recipes from search results that are already ingredients of the given recipe
   */
  get filterSubRecipes() {
    let filtered: RecipeReduced[] = [];
    this.searchResults.recipes.map((sub) => {
      if (sub.id !== this.recipesManagementState.recipe.id) {
        filtered.push(sub);
      }
    });
    return filtered;
  }

  get servingSizeMeasurementOptions() {
    return this.allIngredientsHaveLiquidVolumes
      ? [
        { id: IngredientMeasurementEnum.Gram.toString(), name: "g" },
        { id: IngredientMeasurementEnum.Milliliter.toString(), name: "ml" },
        { id: IngredientMeasurementEnum.Ounce.toString(), name: "oz" },
        { id: IngredientMeasurementEnum.FluidOunce.toString(), name: "fl. oz" },
      ]
      : [
        { id: IngredientMeasurementEnum.Gram.toString(), name: "g" },
        { id: IngredientMeasurementEnum.Ounce.toString(), name: "oz" },
      ];
  }

  isTrialUser() {
    return this.accountState.userDetails.isTrial;
  }

  selectOption(option: { name: string; value: string }) {
    this.selectedOption = option.name;
  }

  checkForLiquidVolumes() {
    // Check if all ingredients and sub recipes of the recipe have liquid measurements for the serving size dropdown options
    this.allIngredientsHaveLiquidVolumes =
      this.recipesManagementState.recipe.ingredients.filter((element: any) => {
        return element.ingredient.hasVolumeUnitOfMeasurement === false;
      }).length > 0 ||
      this.recipesManagementState.recipe.childRecipes.filter((element: any) => {
        return element.hasVolumeUnitOfMeasurement === false;
      }).length > 0
        ? false
        : true;
  }
  // Select option for primary food group
  getPrimaryFoodGroupName(ingr: any): string {
    const foodGroup: SelectObj = _.has(ingr.foodGroupClassification, "class")
      ? this.foodGroupClasses.find(
        (x) => x.name == ingr.foodGroupClassification.class
      )!
      : { id: 0, name: "None Applicable" };
    return foodGroup.name;
  }

  getSelectedServingWeight(measurement: string) {
    this.selectedServingWeightMeasurement.name = measurement;
    switch (measurement) {
    case "g":
      this.selectedServingWeightMeasurement.id = IngredientMeasurementEnum.Gram.toString();
      break;

    case "ml":
      this.selectedServingWeightMeasurement.id = IngredientMeasurementEnum.Milliliter.toString();
      break;

    case "oz":
      this.selectedServingWeightMeasurement.id = IngredientMeasurementEnum.Ounce.toString();
      break;

    case "fl. oz":
      this.selectedServingWeightMeasurement.id = IngredientMeasurementEnum.FluidOunce.toString();
      break;
    }
  }

  getRecipeMetaData(recipeId: number) {
    RecipesRepository.getRecipeMetaData(recipeId)
      .then((response) => {
        this.recipeMetadata = response.data;
        this.recipeMetadata.showAllergenOnPrintout = Boolean(response.data.showAllergenOnPrintout);
        const ruleIndex = response.data.roundingRuleIndex || 0;
        const selectedRule = this.nutritionRules.find((rule) => rule.id === ruleIndex);

        if (selectedRule) {
          this.selectedRoundingRule = selectedRule;
        }

        this.recipeMetadata.roundingRuleIndex = ruleIndex;

        if(this.recipeMetadata.isSupplementLabel) {
          this.isSupplementLabelLoaded = true;
        }

      })
      .catch(() => {
      });
  }

  async loadRecipeDetails() {
    this.recipesManagementState.loadingRecipe = true;
    try {
      const response: any = await RecipesRepository.getRecipeDetails(parseInt(this.$route.params.id), true);

      if (response) {
        this.mapRecipeDetails(response.data);
        await this.ensureRecipeNutritionCountryLoaded(response.data.nutritionCountry);
        await this.loadAllergenConfiguration(response.data.id);
        await this.hydrateRecipeStatusesFromSavedAnalysis(response.data.id);
      }
      else {
        this.recipeSaved = false;
        this.$router.push({ name: "recipesAdd" });
      }

      this.loadRecipeComponents();
      void this.maybeOpenExportLabelFromQuery();
    }
    catch (error: any) {
      if (error?.response?.status === 404) {
        this.$snotify.error(
          error.response.data?.errorMessages?.[0]?.userVisibleMessage ||
            this.translations.get("item-not-found")
        );
        this.$router.replace({ name: "recipes" });
        return;
      }

      this.$snotify.error(
        error?.response?.data?.errorMessages?.[0]?.userVisibleMessage ||
          error?.response?.data?.message ||
          this.translations.get("network-error")
      );
    }
    finally {
      this.recipesManagementState.loadingRecipe = false;
    }
  }

  mapRecipeDetails(recipeResponse : Recipe){
    this.calculationMethodModal.selectedDailyValueCalculation = recipeResponse.dailyValueCalculationMethod;
    this.calculationMethodModal.selectedProteinDailyValueCalculation = recipeResponse.proteinDvCalculationMethod;
    this.calculationMethodModal.selectedCalculationMethod = recipeResponse.caloriesCalculationMethod;
    this.calculationMethodModal.pdcaasScore = recipeResponse.pdcaasScore;

    this.recipesManagementState.recipe = {
      ...this.recipesManagementState.recipe,
      ...recipeResponse
    };
    this.fillNutritionCountry(recipeResponse.nutritionCountry);
    this.getRecipeMetaData(recipeResponse.id);

    this.recipesManagementState.recipe.nutritionAnalysis = mapNutritions(
      recipeResponse.nutritionAnalysis
    );
    this.recipesManagementState.recipe.referenceAmount = recipeResponse.referenceAmount;
    this.recipesManagementState.recipe.productTypeId = recipeResponse.productTypeId;

    if(this.foodProductTypes.length > 0) {
      this.recipesManagementState.recipe.recipeProductType = this.foodProductTypes.find(
        (x) => x.id === recipeResponse.productTypeId
      ) || createDefaultProductType();
    }

    this.resetServingSizeModal();
    this.recipesManagementState.recipe.ingredients.map(
      (ingredient) => {
        if (ingredient.ingredient) {
          ingredient.ingredient.hasAllergenAnalysis = Boolean(ingredient.ingredient.hasAllergenAnalysis);
          ingredient.ingredient.hasAllergen = Boolean(ingredient.ingredient.hasAllergen);
        }

        if (ingredient.foodGroupClassification == null) {
          ingredient.foodGroupClassification = {
            relatedFoodGroupClassifications: [],
          };
        }

        else {
          // RelatedFoodGroupClassifications needs to have a name and id to fit the select requirements
          ingredient.foodGroupClassification.relatedFoodGroupClassifications = (
            this.transformIntoSelectObject(
              ingredient.foodGroupClassification.relatedFoodGroupClassifications
            )
          );
        }

        return;
      }
    );

    this.recipesManagementState.recipe.childRecipes.map(
      (childRecipe: any) => {
        if (childRecipe.recipe) {
          childRecipe.recipe.hasAllergenAnalysis = Boolean(childRecipe.recipe.hasAllergenAnalysis);
          childRecipe.recipe.hasAllergen = Boolean(childRecipe.recipe.hasAllergen);
        }

        if (childRecipe.foodGroupClassification == null) {
          childRecipe.foodGroupClassification = {
            relatedFoodGroupClassifications: [],
          };
        }

        else {
          childRecipe.foodGroupClassification.relatedFoodGroupClassifications =
            this.transformIntoSelectObject(
              childRecipe.foodGroupClassification.relatedFoodGroupClassifications
            );
        }
        return;
      }
    );

    this.fetchEnabledClaims();
  }

  private fillNutritionCountry(nutritionCountryId: any) {
    const nutritionCountry = this.nutritionCountries.find((x: any) => x.id === nutritionCountryId);
    if (nutritionCountry) {
      this.recipesManagementState.recipe.nutritionCountry = nutritionCountry;
      this.fetchPackageTypes(this.countryId);
    }
  }

  private async ensureRecipeNutritionCountryLoaded(nutritionCountryId: any) {
    if (!nutritionCountryId || this.recipesManagementState.recipe.nutritionCountry?.id === nutritionCountryId) {
      return;
    }

    if (!this.nutritionCountries.length) {
      await this.fetchNutritionCountries();
    }

    this.fillNutritionCountry(nutritionCountryId);
  }

  loadRecipeComponents(){
    this.setSubFoodGroupsMatrix();
    this.setRecipeCookingMethodAndLoss();
    this.checkForLiquidVolumes();
  }

  fetchCategories() {
    RecipesRepository.listRecipeCategories().then((response) => {
      response.data.result && response.data.result.length
        ? (this.categories = [
          { id: -1, name: "Add new category" },
          ...response.data.result,
        ])
        : (this.categories = [{ id: -1, name: "Add new category" }]);
    });
  }

  fetchPackageTypes(countryId: number = 0){
    RecipesRepository.getProductTypesByCountry(countryId).then((response) => {
      if(response){
        this.foodProductTypes = [
          ...response.data,
        ];

        this.recipesManagementState.recipe.recipeProductType = response.data.find(
          (x: RecipeProductType) => x.id == this.recipesManagementState.recipe.productTypeId
        );

        response.data.find((x: RecipeProductType) => x.id == this.recipesManagementState.recipe.productTypeId);
      }
      else{
        this.foodProductTypes = [];
        this.recipesManagementState.recipe.recipeProductType = createDefaultProductType();
      }
    });
  }

  selectProductType(productType: RecipeProductType) {
    this.recipesManagementState.recipe.recipeProductType = productType;
    this.calculateNutritions();
    this.recipeSaved = false;
  }

  selectCategory(category: { id: number; name: string; recipes?: any }) {
    // If add category is clicked -> open modal, else -> assign value from the selected element to recipe category
    category.id == -1
      ? (this.showCategoryModal = true)
      : (this.recipesManagementState.recipe.recipeCategory = category);
  }

  addCategory() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_category_adding"),
      () =>
        new Promise((resolve, reject) => {
          RecipesRepository.createRecipeCategory({
            name: this.recipesManagementState.addCategory.name,
          })
            .then((response) => {
              resolve({
                body: this.translations.get("rec_category_added"),
              });

              this.categories.push({
                id: response.data.result.id,
                name: response.data.result.name,
                recipes: [],
              });
              this.recipesManagementState.recipe.recipeCategory = {
                ...response.data.result,
              };

              // reset fields
              this.recipesManagementState.addCategory = { id: 0, name: "" };
              this.showCategoryModal = false;
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  cancelAddCategory() {
    this.showCategoryModal = false;
    this.recipesManagementState.addCategory = { id: 0, name: "" };
  }

  openSearchIngredientsModal() {
    this.showIngredientModal = true;
    this.numberOfSearchClicks = 0;
  }

  openClaimsModal(){
    this.showClaimModal = true;
  }
  cancelTrailUserModal(){
    this.showTrialUserModal = false;
  }

  async checkTrialExportLabelAvailability(): Promise<{ canAccess: boolean; shouldConfirm: boolean }> {
    try {
      const response = await RestaurantRepository.getCurrentTrialMetadata();

      if (response.data.hasGeneratedFirstLabel) {
        this.showTrialUpsellModal = true;
        return {
          canAccess: false,
          shouldConfirm: false,
        };
      }

      return {
        canAccess: true,
        shouldConfirm: true,
      };
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(
          error.response.data?.errorMessages?.[0]?.userVisibleMessage ||
            error.response.data?.message
        );
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }

      return {
        canAccess: false,
        shouldConfirm: false,
      };
    }
  }

  async confirmTrialExportLabel() {
    if (this.isConfirmingTrialExportLabel) {
      return;
    }

    this.isConfirmingTrialExportLabel = true;

    try {
      await RestaurantRepository.updateCurrentTrialActivity({
        activity: TrialRestaurantActivity.GeneratedFirstLabel,
      });

      this.showTrialExportLabelConfirmationModal = false;

      if (this.accountState.userDetails.nutritionLabelsLimit.labelsLimit == null) {
        await this.updatePrintMeta();
      }

      this.showExportLabelModal = true;
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(
          error.response.data?.errorMessages?.[0]?.userVisibleMessage ||
            error.response.data?.message
        );
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
    finally {
      this.isConfirmingTrialExportLabel = false;
    }
  }

  async markTrialActivityCompleted(activity: TrialRestaurantActivity) {
    if (!this.accountState.userDetails.isTrial) {
      return;
    }

    try {
      await RestaurantRepository.updateCurrentTrialActivity({ activity });
    }
    catch {
      // Ignore trial metadata update failures to avoid interrupting the main flow.
    }
  }

  nutrientSourceName(ingr: any) {
    if (ingr.nutrientSource != null && ingr.nutrientSource <= 4) {
      return this.nutrientCountryNames[ingr.nutrientSource];
    }
    else if (ingr.nutrientSource != null && ingr.nutrientSource == 5) {
      return "Sugar Alcohols";
    }
    else if (ingr.nutrientSource != null && ingr.nutrientSource == 6) {
      return "FNDDS";
    }
    else {
      return "Custom Ingredients";
    }
  }

  shouldShowNutrientSource(nutrientSource: NutrientSource) {
    return this.nutrientSources.indexOf(nutrientSource) > -1;
  }

  setAllSearchDatabases(isSelected: boolean) {
    this.includeCustomIngredients = isSelected;
    this.nutrientsListForSearch = isSelected
      ? [...this.availableSearchDatabases]
      : [];
  }

  setAllUSDANutrientDatabases(isSelected: boolean) {
    if (isSelected) {
      const mergedSources = new Set([
        ...this.nutrientsListForSearch,
        ...this.availableUsdaNutrientSources,
      ]);
      this.nutrientsListForSearch = Array.from(mergedSources);
      return;
    }

    this.nutrientsListForSearch = this.nutrientsListForSearch.filter(
      (source) => !this.availableUsdaNutrientSources.includes(source as NutrientSource)
    );
  }
  /**
   *  check for spelling errors on enetered serach term
   */
  checkForSpelling() {
    lodashDebounce(() => {
      let serachList = this.ingredientSearchString.trim().split(" ");
      IngredientsRepository.getAutoCorrectSerchTerm(serachList).then(
        (response) => {
          this.autoCorrectionHtml = "";
          this.autoCorrection = "";
          let term = response.data.result;
          this.hasAutoCorrection = false;
          for (let i = 0; i < term.length; i++) {
            let word = term[i];
            if (word.isCorrected) {
              this.hasAutoCorrection = true;
              this.autoCorrectionHtml +=
                '<span class="text-auto-correct">' +
                word.searchTerm +
                "</span>";
              this.autoCorrection += word.searchTerm;
            }
            else {
              this.autoCorrectionHtml += word.searchTerm;
              this.autoCorrection += word.searchTerm;
            }
            if (i < term.length - 1) {
              this.autoCorrectionHtml += " ";
              this.autoCorrection += " ";
            }
          }
        }
      );
    }, 600);
  }
  /** change search term with corrected term
   */
  onCorrectSerachTerm() {
    this.ingredientSearchString = this.autoCorrection;
    this.hasAutoCorrection = false;
    this.autoCorrectionHtml = "";
    this.autoCorrection = "";
  }
  /**
   *  add class to table when a dropdown is opened so it can overflow the container
   */
  openMenu() {
    const recipeIngredientsCard = document.getElementById("recipe-ingredients");
    let moistureLossTable = document.getElementById(
      "table-moisture-loss-classification"
    );
    let recipeIngredientsTable = document.getElementById(
      "ingr-and-recipe-table"
    );
    let searchIngredientsTable = document.getElementById("search-ingr-table");

    if (moistureLossTable) {
      moistureLossTable!.classList.add("overflow-visible");
      moistureLossTable!.classList.remove("overflow-hidden");
    }
    if (recipeIngredientsCard) {
      recipeIngredientsCard.classList.add("dropdown-layer-active");
    }
    recipeIngredientsTable!.classList.add("overflow-visible");
    recipeIngredientsTable!.classList.remove("overflow-hidden");
    if (searchIngredientsTable) {
      searchIngredientsTable!.classList.add("overflow-visible");
      searchIngredientsTable!.classList.remove("overflow-hidden");
    }
  }
  /**
   *  remove class from table when a dropdown is closed so it can be scrolled horizontally on small devices
   */
  closeMenu() {
    const recipeIngredientsCard = document.getElementById("recipe-ingredients");
    let moistureLossTable = document.getElementById(
      "table-moisture-loss-classification"
    );
    let recipeIngredientsTable = document.getElementById(
      "ingr-and-recipe-table"
    );
    let searchIngredientsTable = document.getElementById("search-ingr-table");

    if (moistureLossTable) {
      moistureLossTable!.classList.add("overflow-hidden");
      moistureLossTable!.classList.remove("overflow-visible");
    }
    if (recipeIngredientsCard) {
      recipeIngredientsCard.classList.remove("dropdown-layer-active");
    }
    recipeIngredientsTable!.classList.add("overflow-hidden");
    recipeIngredientsTable!.classList.remove("overflow-visible");
    if (searchIngredientsTable) {
      searchIngredientsTable!.classList.add("overflow-hidden");
      searchIngredientsTable!.classList.remove("overflow-visible");
    }
  }
  /**
   *  keep track of how many times the user makes a search but does not select any of the ingredients
   */
  sendIngredientSearchFailure() {
    if (this.numberOfSearchClicks > 0) {
      IngredientsRepository.createIngredientSearchFailure({
        searchTerm: this.previousSearchTerm,
        searchResultsCount: this.totalResults,
      });
    }
  }

  searchIngredients() {
    this.sendIngredientSearchFailure();
    this.searchLoading = true;
    this.noResult = false;
    this.numberOfSearchClicks += 1;

    if (
      this.numberOfSearchClicks > 3 &&
      this.accountState.userDetails.isTrial
    ) {
      this.showZendesk();
    }

    this.previousSearchTerm = this.ingredientSearchString;

    const model: SearchIngredients = {
      nutrientSources: this.nutrientsListForSearch,
      maxResultsCount: 200,
      includeCustomIngredients: this.includeCustomIngredients,
      searchTerm: this.ingredientSearchString,
      recipeId:
        this.recipesManagementState.recipe.id > 0
          ? this.recipesManagementState.recipe.id
          : null,
    };

    // if (this.selectedOption === "Ingredient name") {
    IngredientsRepository.searchIngredients(model)
      .then((response) => {
        this.handleIngredientResponse(response);
      })
      .catch((error) => {
        this.handleIngredientSearchError(error);
      })
      .finally(() => {
        this.searchLoading = false;
      });
    // }
    // else if (this.selectedOption === "Food code") {
    //   IngredientsRepository.searchIngredientsByFoodCode(model).then(
    //     (response) => {
    //       this.handleIngredientResponse(response);
    //     }
    //   );
    // }
  }

  handleIngredientResponse(response: any) {
    this.resetIngredientSearchResults();

    if (response.data.result.dbIngredients !== null) {
      response.data.result.dbIngredients.map((ingr: IngredientReduced) => {
        ingr.measurement = { ...ingr.allMeasurements[0] };
        ingr.isCustomIngredient = false;
        this.searchResults.ingredients.push(ingr);
      });
    }

    if (response.data.result.customIngredients != null) {
      response.data.result.customIngredients.map((ingr: IngredientReduced) => {
        ingr.measurement = { ...ingr.allMeasurements[0] };
        ingr.isCustomIngredient = true;
        this.searchResults.customIngredients.push(ingr);
      });
    }

    if (response.data.result.recipes !== null) {
      this.searchResults.recipes = response.data.result.recipes;
    }

    if (
      response.data.result.dbIngredients.length === 0 &&
      response.data.result.customIngredients.length === 0 &&
      response.data.result.recipes.length === 0
    ) {
      this.noResult = true;
    }
    else {
      this.noResult = false;
    }
  }

  handleIngredientSearchError(error: any) {
    this.noResult = false;
    this.$snotify.error(getErrorMessage(error, this.translations, "network_error"));
  }

  selectMeasurementInAddIngredient(
    measurement: { id: number; name: string; weightInGrams: number },
    ingredient: IngredientReduced
  ) {
    ingredient.measurement = { ...measurement };
    this.activeIngredientSearchMeasurementDropdownKey = null;
  }
  setActiveIngredientSearchMeasurementDropdown(dropdownKey: string) {
    this.activeIngredientSearchMeasurementDropdownKey = dropdownKey;
  }
  clearActiveIngredientSearchMeasurementDropdown(dropdownKey?: string) {
    if (
      dropdownKey == null ||
      this.activeIngredientSearchMeasurementDropdownKey === dropdownKey
    ) {
      this.activeIngredientSearchMeasurementDropdownKey = null;
    }
  }
  isIngredientSearchMeasurementDropdownActive(dropdownKey: string) {
    return this.activeIngredientSearchMeasurementDropdownKey === dropdownKey;
  }
  openAddIngredientModal() {
    this.showAddIngredientModal = true;
    this.addCustomIngredientModalTitle = this.translations.get(
      "ingr_modal_add_title"
    );
  }
  submitNewIngredient(
    ingredient: Ingredient,
    nutrients: {
      value: number;
      nutrientUnitId: number;
    }[],
    selectedAllergens?: SavedAllergen[],
    mayContainText?: Partial<Record<LabelLanguage, string[]>>,
    options?: { openAiAfterSave?: boolean }
  ) {
    this.newCustomIngredient.allergens = [...(selectedAllergens || ingredient?.allergens || [])];
    this.newCustomIngredient.mayContainText = { ...(mayContainText || ingredient?.mayContainText || {}) };

    const existingIngredientId = this.newCustomIngredient.id;

    if (existingIngredientId) {
      const updateIngredient = buildUpdateRestaurantIngredientPayload(
        this.newCustomIngredient,
        nutrients
      );

      // @ts-ignore
      this.$snotify.async(
        this.translations.get("ingr_toastr_updating"),
        () =>
          new Promise((resolve, reject) => {
            IngredientsRepository.updateRestaurantIngredient(updateIngredient)
              .then(async() => {
                await this.saveIngredientAllergenConfiguration(
                  updateIngredient.id,
                  this.newCustomIngredient.allergens,
                  this.newCustomIngredient.mayContainText
                );

                if (options?.openAiAfterSave) {
                  const ingredientResponse = await IngredientsRepository.getIngredientDetails(existingIngredientId);

                  this.newCustomIngredient = { ...ingredientResponse.data.result };
                  this.addCustomIngredientModalTitle = this.translations.get("ingr_modal_edit_title");
                  this.ingredientSearchString = ingredientResponse.data.result.name;
                  this.postSaveCustomIngredientAiApplyToken += 1;
                  this.searchIngredients();

                  resolve({
                    body: "Custom ingredient saved successfully. Running allergen analysis...",
                  });

                  return;
                }

                this.showAddIngredientModal = false;
                this.ingredientSearchString = updateIngredient.name;
                this.searchIngredients();

                resolve({
                  body: this.translations.get(
                    "ingr_toastr_updated",
                    updateIngredient.name
                  ),
                });
                this.newCustomIngredient = createDefaultIngredient();
              })
              .catch((error) => {
                reject({ body: getErrorMessage(error, this.translations, "network_error") });
              });
          }),
        {
          timeout: 3000,
          closeOnClick: true
        }
      );
      return;
    }

    let newIngredient = buildCreateRestaurantIngredientPayload(
      this.newCustomIngredient,
      nutrients
    );

    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr_toastr_adding"),
      () =>
        new Promise((resolve, reject) => {
          IngredientsRepository.createRestaurantIngredient(newIngredient)
            .then(async(response) => {
              const ingredientId = getCreatedIngredientId(response);

              if (ingredientId !== null) {
                await this.saveIngredientAllergenConfiguration(
                  ingredientId,
                  this.newCustomIngredient.allergens,
                  this.newCustomIngredient.mayContainText
                );
              }

              if (options?.openAiAfterSave && ingredientId) {
                const ingredientResponse = await IngredientsRepository.getIngredientDetails(ingredientId);

                this.newCustomIngredient = { ...ingredientResponse.data.result };
                this.addCustomIngredientModalTitle = this.translations.get("ingr_modal_edit_title");
                this.ingredientSearchString = ingredientResponse.data.result.name;
                this.postSaveCustomIngredientAiApplyToken += 1;
                this.searchIngredients();

                resolve({
                  body: "Custom ingredient saved successfully. Running allergen analysis...",
                });

                return;
              }

              this.showAddIngredientModal = false;
              this.ingredientSearchString = response.data.result.name;
              this.searchIngredients();

              resolve({
                body: this.translations.get(
                  "ingr_toastr_added",
                  newIngredient.name
                ),
              });
              this.newCustomIngredient = createDefaultIngredient();
            })
            .catch((error) => {
              reject({ body: getErrorMessage(error, this.translations, "network_error") });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  closeAddIngredientModal() {
    this.showAddIngredientModal = false;
    this.newCustomIngredient = createDefaultIngredient();
  }

  async addIngredient(ingr: IngredientReduced) {
    if (ingr) {
      try {
        const [ingredientResponse, allergenConfigurationResponse] = await Promise.all([
          IngredientsRepository.getIngredientDetails(ingr.id),
          ingr.isCustomIngredient
            ? AllergenRepository.getStoredAllergenConfiguration(
              AllergenConfigurationTargetType.Ingredient,
              ingr.id
            )
            : Promise.resolve(null)
        ]);

        const fullIngredient: Ingredient = ingredientResponse.data.result;
        const ingredientConfiguration = allergenConfigurationResponse?.data || null;
        const hasIngredientAllergenConfiguration = Boolean(
          ingredientConfiguration
            && (
              (ingredientConfiguration.allergens || []).length > 0
              || Object.values(ingredientConfiguration.mayContainText || {}).some((items) => ((items as string[] | undefined) || []).length > 0)
            )
        );

        this.recipesManagementState.recipe.ingredients.push({
          ingredient: fullIngredient,
          measurement: ingr.measurement
            ? ingr.measurement
            : ingr.allMeasurements[0],
          quantity: 1,
          isAddedSugar: ingr.isAddedSugar,
          foodGroupClassification: { relatedFoodGroupClassifications: [] },
          foodGroupClassificationId: null,
          nutrientCoeficient:0
        });

        if (hasIngredientAllergenConfiguration) {
          const shouldApplyIngredientAllergenConfiguration = await this.openIncludeAllergenConfigurationModal(
            "This custom ingredient has saved allergen and may contain information.",
            "Do you want to include this information in this recipe?"
          );

          if (shouldApplyIngredientAllergenConfiguration) {
            this.mergeIngredientAllergenConfigurationIntoRecipe(ingredientConfiguration!);
          }
        }

        // Check if the newly added ingredient has a liquid volume measurement, if it doesn't then change the serving size to serving count
        if (
          !fullIngredient.hasVolumeUnitOfMeasurement &&
          (this.selectedServingWeightMeasurement.id ==
            IngredientMeasurementEnum.Milliliter.toString() ||
            this.selectedServingWeightMeasurement.id ==
            IngredientMeasurementEnum.FluidOunce.toString())
        ) {
          this.servingCount = 1;
          this.setRecipeServingCount();
        }

        else {
          // this.calculateNutritions();
        }

        this.disableButtonsBeforeSave();
        void this.markTrialActivityCompleted(
          TrialRestaurantActivity.TriedAddingIngredientToFirstRecipe
        );
        this.submit("", null);
      }
      catch {
        this.$snotify.error(this.translations.get("network_error"));
      }
    }
  }

  async addSubRecipe(recipe: RecipeReduced) {
    if (recipe) {
      const [recipeResponse, allergenConfigurationResponse] = await Promise.all([
        RecipesRepository.getRecipeDetails(recipe.id, true),
        AllergenRepository.getStoredAllergenConfiguration(
          AllergenConfigurationTargetType.Recipe,
          recipe.id
        ).catch((): null => null)
      ]);

      recipeResponse.data.nutritionAnalysis = mapNutritions(
        recipeResponse.data.nutritionAnalysis
      );

      const recipeAllergenConfiguration = allergenConfigurationResponse?.data || null;
      const hasRecipeAllergenConfiguration = Boolean(
        recipeAllergenConfiguration
          && (
            (recipeAllergenConfiguration.allergens || []).length > 0
            || Object.values(recipeAllergenConfiguration.mayContainText || {}).some(
              (items) => ((items as string[] | undefined) || []).length > 0
            )
          )
      );

      let recipeCompositionMeasurementId = this.subrecipeServingSizes.find(
        (rcm: RecipeCompositionMeasurement) => recipe.measurement && rcm.measurement.id === recipe.measurement.id
      );

      if (recipeCompositionMeasurementId == null ) {
        recipeCompositionMeasurementId = this.subrecipeServingSizes[0];
      }

      this.recipesManagementState.recipe.childRecipes.push({
        recipe: recipeResponse.data,
        quantity: 1,
        isAddedSugar: recipe.isAddedSugar,
        weightPerServing: recipeResponse.data.weightPerServing,
        recipeCompositionMeasurementId: recipeCompositionMeasurementId.id,
        id: recipe.id, // TODO: Need to verify.
        foodGroupClassificationId: null,
        hasVolumeUnitOfMeasurement: recipe.hasVolumeUnitOfMeasurement
      });

      if (hasRecipeAllergenConfiguration) {
        const shouldApplyRecipeAllergenConfiguration = await this.openIncludeAllergenConfigurationModal(
          "This recipe has saved allergen and may contain information.",
          "Do you want to include this information in this recipe?"
        );

        if (shouldApplyRecipeAllergenConfiguration) {
          this.mergeIngredientAllergenConfigurationIntoRecipe(recipeAllergenConfiguration);
        }
      }

      // Check if the newly added recipe has a liquid volume measurement, if it doesn't,
      // then change the serving size to serving count
      if (
        this.selectedServingWeightMeasurement.id == IngredientMeasurementEnum.Milliliter.toString()
        || this.selectedServingWeightMeasurement.id == IngredientMeasurementEnum.FluidOunce.toString()
      ) {
        this.servingCount = 1;
        this.setRecipeServingCount();
      }
      else {
        // this.calculateNutritions();
      }

      this.disableButtonsBeforeSave();
      this.skipAllergenReloadOnReset = true;
      this.submit("", null);
      // this.resetState();
    }
  }

  selectSubRecipeMeasurement(
    measurement: { id: number; name: string },
    subRecipe: any
  ) {
    // Find the RecipeCompositionMeasurement object that corresponds to the selected measurement
    const measurementObj = this.subrecipeServingSizes.find(
      (rcm: RecipeCompositionMeasurement) => rcm.measurement.id === measurement.id
    );

    subRecipe.recipeCompositionMeasurementId = measurementObj.id;
    this.calculateNutritions();
  }

  selectSubRecipeMeasurementInAddIngredient(
    measurement: { id: number; name: string; weightInGrams: number },
    subRecipe: RecipeReduced
  ) {
    subRecipe.measurement = { ...measurement };
    this.activeIngredientSearchMeasurementDropdownKey = null;
  }

  getSubrecipeMeasurementByCompositionMeasurementId(compositionMeasurementId: number) {
    return this.subrecipeServingSizes.find(
      (rcm: RecipeCompositionMeasurement) => rcm.id === compositionMeasurementId
    )!.measurement;
  }

  fetchSubrecipeServingSizes() {
    RecipesRepository.getRecipeCompositionMeasurements().then((response) => {
      this.subrecipeServingSizes = response.data.result;

      this.subrecipeServingMeasurements = this.subrecipeServingSizes.map(
        (measurement: RecipeCompositionMeasurement) => measurement.measurement
      );
    });
  }

  async fetchNutritionCountries(){
    const response = await NutritionLabelRepository.listNutritionCountries();
    this.nutritionCountries = response.data.result;
  }

  getRawRecipeWeight() {
    return round(this.recipesManagementState.recipe.weight, 2);
    // return this.recipesManagementState.recipe.nutritionAnalysis.getRawRecipeWeight(
    //   this.recipesManagementState.recipe.recipeServingType
    // );
  }

  formatSearchResultCalories(value: number | null | undefined) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }

    return Number(value).toFixed(2);
  }

  closeSearchIngredientsModal() {
    this.showIngredientModal = false;
    this.resetState();
    this.sendIngredientSearchFailure();
  }
  onDoneBtn() {
    this.showIngredientModal = false;
    this.resetState();
    this.sendIngredientSearchFailure();
    (<any>window).zE("webWidget", "close");
  }
  selectMeasurementForMoistureLoss(measurement: SelectObj) {
    this.moistureLossSelectedMeasurement = { ...measurement };
  }
  selectMeasurementForRecipeWeightLoss(measurement: SelectObj) {
    this.recipeWeightLossSelectedMeasurement = { ...measurement };
  }
  selectCookingMethod(cookingMethod: SelectObj) {
    this.selectedCookingMethod = { ...cookingMethod };
  }
  selectPrimaryFoodGroup(foodGroup: SelectObj, ingredient: any) {
    ingredient.foodGroupClassification.class = foodGroup.name;
    ingredient.foodGroupClassification.id = foodGroup.id;

    const classificationName =
      foodGroup != null ? foodGroup.name : "None Applicable";
    if (classificationName != "None Applicable") {
      ingredient.foodGroupClassification.subClass = foodGroup.name;
    }
    const includedInMatrix = this.foodGroupClassesMatrix.find(
      (x) => x.class == classificationName
    );

    if (
      includedInMatrix != null &&
      includedInMatrix.subClass != null &&
      includedInMatrix.subClass.length
    ) {
      // set sub classifications for ingredient
      let subClassifications = this.foodGroupClassesMatrix.find(
        (x) => x.class == classificationName
      )!.subClass;

      ingredient.foodGroupClassification.relatedFoodGroupClassifications = this.transformIntoSelectObject(
        subClassifications
      );

      ingredient.foodGroupClassification.id = ingredient.foodGroupClassification
        .relatedFoodGroupClassifications.length
        ? ingredient.foodGroupClassification.relatedFoodGroupClassifications[0].id
        : -1;

      this.$forceUpdate();
    }

    else {
      //  Send request to get sub classification, set them to the ingredient and set them in the matrix
      RecipesRepository.listFoodGroupSubClasses({
        PrimaryClass: classificationName,
      }).then((response) => {
        const foodGroupSubClasses = response.data.result;
        this.foodGroupClassesMatrix.push({
          class: classificationName,
          subClass: [...foodGroupSubClasses],
        });
        ingredient.foodGroupClassification.relatedFoodGroupClassifications =
          this.transformIntoSelectObject(foodGroupSubClasses);
        ingredient.foodGroupClassification.id = ingredient
          .foodGroupClassification.relatedFoodGroupClassifications.length
          ? ingredient.foodGroupClassification
            .relatedFoodGroupClassifications[0].id
          : -1;
        this.$forceUpdate();
      });
    }
  }
  selectSecondaryFoodGroup(
    subClassification: SelectObj,
    ingredient: RecipeIngredient
  ) {
    ingredient.foodGroupClassification.id = subClassification.id;
  }
  selectMeasurement(
    measurement: { id: number; name: string; weightInGrams: number },
    ingredient: RecipeIngredient
  ) {
    ingredient.measurement = { ...measurement };
    this.calculateNutritions();
    this.disableButtonsBeforeSave();
  }
  setRecipeCookingMethodAndLoss() {
    if (this.recipesManagementState.recipe) {
      if (this.recipesManagementState.recipe.lossAmount != null) {
        this.moistureLossValue = round(this.recipesManagementState.recipe.lossAmount, 2);
      }

      if (this.recipesManagementState.recipe.lossType != null) {
        let measurementObj: SelectObj =
          this.moistureLossMeasurementOptions.find(
            (m: any) => m.id === this.recipesManagementState.recipe.lossType
          )!;
        this.selectMeasurementForMoistureLoss(measurementObj);
        //TODO for Recipe Weight Loss
      }

      if (this.recipesManagementState.recipe.cookingMethodId == null)
        this.recipesManagementState.recipe.cookingMethodId = -1;
      const cookingMethod: SelectObj = this.cookingMethods.find(
        (cookingMethod: any) =>
          cookingMethod.id ===
          this.recipesManagementState.recipe.cookingMethodId
      )!;
      this.selectCookingMethod(cookingMethod);
    }
  }
  setSubFoodGroupsMatrix() {
    // Food classification
    const classificationsArray: any[] = [];
    if (
      this.recipesManagementState.recipe &&
      this.recipesManagementState.recipe.ingredients != null &&
      this.recipesManagementState.recipe.ingredients.length
    ) {
      for (const ingredient of this.recipesManagementState.recipe.ingredients) {
        let ingredientClass =
          ingredient.foodGroupClassification != null
            ? ingredient.foodGroupClassification.class
            : "";
        let ingredientSubclass =
          ingredient.foodGroupClassification != null &&
          ingredient.foodGroupClassification.relatedFoodGroupClassifications !=
          null
            ? ingredient.foodGroupClassification.relatedFoodGroupClassifications
            : [];
        if (
          !classificationsArray.some((x: any) => x.class === ingredientClass)
        ) {
          classificationsArray.push({
            class: ingredientClass,
            subClass: ingredientSubclass,
          });
        }
        else {
          if (ingredientSubclass.length) {
            classificationsArray.find(
              (x) => x.class === ingredientClass
            ).subClass = [...ingredientSubclass];
          }
        }
      }
    }
    this.foodGroupClassesMatrix = [...classificationsArray];
  }

  isScaledRecipeServingSizeValid(scaledRecipeServingSize : number) {
    if (scaledRecipeServingSize != null && scaledRecipeServingSize > 0) {
      return true;
    }
    else return false;
  }

  scaleRecipe() {
    if (!this.isScaledRecipeServingSizeValid(this.scaledRecipeServingSize)) {
      // Don't allow negative values or zero
      this.$snotify.error(this.translations.get("rec_invalid_scaling_value"));
      return;
    }
    if (this.servingCount != null && this.servingCount != 0) {
      let scalingCoefficient = this.scaledRecipeServingSize / this.servingCount;
      if (this.recipesManagementState.recipe.ingredients.length > 0) {
        // Multiply the quantity of each ingredient by the coefficient so that the size per serving stays exactly the same
        this.recipesManagementState.recipe.ingredients.map(
          (ingr: RecipeIngredient) =>
            (ingr.quantity = round(ingr.quantity * scalingCoefficient, 2))
        );
      }
      if (this.recipesManagementState.recipe.childRecipes.length > 0) {
        // Same goes for quantity of child recipes added as ingredients
        this.recipesManagementState.recipe.childRecipes.map(
          (childRecipe: any) =>
            (childRecipe.quantity = round(
              childRecipe.quantity * scalingCoefficient,
              2
            ))
        );
      }
      this.servingCount = this.scaledRecipeServingSize;
      this.setRecipeServingCount(); // Set the new serving size to be the entered value in the modal
    }
    this.toggleScaleRecipeModal(); // Close modal
  }

  updateMoistureLoss() {
    if (!this.checkIfMoistureLossValueIsValid()) {
      return this.$snotify.error(
        this.translations.get(
          "rec_invalid_moisture_loss_value", this.moistureLossSelectedMeasurement.id == LossType.Percentage ?
            100 : this.getOriginalMoistureInRecipe(),
          this.moistureLossSelectedMeasurement.id == LossType.Percentage ? "%" : "g"
        )
      );
    }

    // if (this.selectedCookingMethod.name == "Not specified") {
    //   return this.$snotify.error(this.translations.get("UpdateCookingMethodSpecification"));
    // }

    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_updating_yield_adjustments"),
      () =>
        new Promise((resolve, reject) => {
          const model = {
            Ingredients: this.recipeCopy.ingredients.map((ingr: any) => {
              return {
                RecipeComponentId: ingr.id,
                FoodGroupClassificationId:
                  ingr.foodGroupClassification != null &&
                  ingr.foodGroupClassification.id != -1
                    ? ingr.foodGroupClassification.id
                    : null,
              };
            }),
            Subrecipes: this.recipeCopy.childRecipes.map((childRecipe: any) => {
              return {
                RecipeComponentId: childRecipe.id,
                FoodGroupClassificationId:
                  childRecipe.foodGroupClassification != null &&
                  childRecipe.foodGroupClassification.id != -1
                    ? childRecipe.foodGroupClassification.id
                    : null,
              };
            }),
            CookingMethodId:
              this.selectedCookingMethod.id > 0
                ? this.selectedCookingMethod.id
                : null,
            LossAmount: Math.abs(this.moistureLossValue),
            LossType: this.moistureLossSelectedMeasurement.id,
            RecipeId: this.recipesManagementState.recipe.id,
          };
          RecipesRepository.updateRecipeFoodClassifications(model)
            .then(() => {
              resolve({
                body: this.translations.get("rec_updated_yield_adjustments"),
              });
              this.loadRecipeDetails();
              this.toggleMoistureLossModal();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  getMoistureWeight() {
    const analysis = this.recipesManagementState.recipe.nutritionAnalysis;

    return (
      (analysis.getExactValue(NutrientType.Moisture) / analysis.moistureYieldFactor)
      / analysis.ingredientScaleMultiplier
    );
  }

  getMoistureDisplayText() {
    return `${this.getOriginalMoistureInRecipe()} g`;
  }

  getOriginalMoistureInRecipe() {
    return round(this.recipesManagementState.recipe.nutritionAnalysis.originalMoistureInRecipe, 2);
  }

  checkIfMoistureLossValueIsValid() {
    // can be 0 for grams and 1 for percent
    const selectedOptionForMoistureLoss = this.moistureLossSelectedMeasurement.id;

    if (selectedOptionForMoistureLoss == LossType.Absolute) {
      // Moisture loss can't be greater than recipe weight in grams
      if (this.moistureLossValue > this.getOriginalMoistureInRecipe()) {
        this.invalidMoistureLossValue = true;
      }
    }
    else {
      // Moisture loss cannot exceed the total moisture percent in the recipe
      // And it must be less than or equal to the total percent of the recipe
      if (selectedOptionForMoistureLoss == LossType.Percentage && this.moistureLossValue > 100 ) {
        this.invalidMoistureLossValue = true;
      }
    }
    return !this.invalidMoistureLossValue;
  }

  setRecipeServingCount() {
    this.recipeServingType = 0;
    // Reset serving weight if customer choose radio button for serving count
    this.recipesManagementState.recipe.servingQuantityInUnitOfMeasurement = null;
    this.selectedServingWeightMeasurement = {
      id: "g",
      name: "g",
    };
    this.calculateNutritions();
  }

  selectServingWeighMeasurement(measurement: ServingWeighMeasurement) {
    this.selectedServingWeightMeasurement = { ...measurement };
  }

  setRecipeServingWeight() {
    this.recipeServingType = 1;
  }

  requestRecipeCountryMetadataUpdate(selectedCountry: NutritionCountry, isSupplementLabel: boolean = false) {
    return this.updateRecipeCountryMetadata(selectedCountry, isSupplementLabel);
  }

  async updateRecipeCountryMetadata(selectedCountry: NutritionCountry, isSupplementLabel: boolean = false) {
    this.recipesManagementState.recipe.nutritionCountry = { ...selectedCountry };
    this.recipeMetadata.isSupplementLabel = isSupplementLabel;

    let recipeMetadata: RecipeMetadata = {
      recipeId: this.recipesManagementState.recipe.id,
      countryId: this.countryId,
      referenceAmount: this.recipesManagementState.recipe.referenceAmount,
      productTypeId: this.recipesManagementState.recipe.productTypeId,
      roundingRuleIndex: 0,
      isSupplementLabel: isSupplementLabel,
      showAllergenOnPrintout: this.recipeMetadata.showAllergenOnPrintout
    };

    this.isUpdatingRecipeCountryMetadata = true;

    try {
      const response: any = await RecipesRepository.updateRecipeCountry(recipeMetadata);

      if (response) {
        this.isSupplementLabelLoaded = isSupplementLabel;
        this.recipesManagementState.recipe.nutritionAnalysis = mapNutritions(response.data);

        if (this.recipesManagementState.recipe.id) {
          await this.loadRecipeDetails();
        }
      }
    }
    finally {
      this.isUpdatingRecipeCountryMetadata = false;

      if (this.pendingRoundingRuleUpdate) {
        const pendingRule = { ...this.pendingRoundingRuleUpdate };
        this.pendingRoundingRuleUpdate = null;
        this.updateRecipeRoundingRulesMetadata(pendingRule);
      }
    }
  }

  updateRecipeIsSupplementState(isSupplementLabel: boolean) {
    this.recipeMetadata.isSupplementLabel = isSupplementLabel;
  }

  async updateShowAllergenOnPrintout(showAllergenOnPrintout: boolean) {
    const previousValue = this.recipeMetadata.showAllergenOnPrintout;
    this.recipeMetadata.showAllergenOnPrintout = showAllergenOnPrintout;

    if (!this.recipesManagementState.recipe.id) {
      return;
    }

    try {
      await RecipesRepository.updateShowAllergenOnPrintout(
        this.recipesManagementState.recipe.id,
        showAllergenOnPrintout
      );
    }
    catch {
      this.recipeMetadata.showAllergenOnPrintout = previousValue;
    }
  }

  updateRecipeRoundingRulesMetadata(selectedRule: RoundingRules) {
    this.recipeMetadata.roundingRuleIndex = selectedRule.id;

    if (this.isUpdatingRecipeCountryMetadata) {
      this.pendingRoundingRuleUpdate = { ...selectedRule };
      return;
    }

    const recipeId = this.recipesManagementState.recipe.id;
    const countryId = this.recipesManagementState.recipe.nutritionCountry?.id;

    if (!recipeId || !countryId) {
      return;
    }

    this.recipesManagementState.loadingRecipe = true;

    RecipesRepository.updateRecipeRoundingRule({
      ruleIndex: selectedRule.id,
      recipeId,
      countryId
    })
      .then(async(response: any) => {
        if (response) {
          this.mapRecipeDetails(response.data);
          await this.ensureRecipeNutritionCountryLoaded(response.data.nutritionCountry);
          await this.hydrateRecipeStatusesFromSavedAnalysis(response.data.id);
        }
      })

      .finally(() => {
        this.recipesManagementState.loadingRecipe = false;
      });
  }


  updateReferenceAmount(){
    this.recipesManagementState.recipe.referenceAmount = this.recipesManagementState.recipe.referenceAmount.toString() != "" ? this.recipesManagementState.recipe.referenceAmount : 0;

    RecipesRepository.updateRecipeReferenceAmount(this.recipesManagementState.recipe.id, this.recipesManagementState.recipe.referenceAmount)
      .then(() => {
      })
      .catch(() => {
      });
  }

  /**
   * When the user updates the "servings" field, even if a recipe is set to make X units per serving,
   * this will set the recipe to make X servings instead.
   */
  updateServingCount() {
    this.recipeServingType = RecipeServingType.Servings;
    // servingCount is updated implicitly
    this.updateServingSize();
  }

  updateServingSize() {
    // @ts-ignore
    this.$snotify.async(
      "Updating recipe's serving size",
      () =>
        new Promise((resolve, reject) => {
          const model: any = {
            recipeId: this.recipesManagementState.recipe.id,
            recipeServingType: this.recipeServingType,
          };

          if (this.recipeServingType === RecipeServingType.Servings) {
            model.servingCount = Math.round(this.servingCount * 100) / 100;
          }

          else {
            model.servingQuantityInUnitOfMeasurement =
              Math.round(this.servingQuantityInUnitOfMeasurement * 100) / 100;

            model.servingUnitOfMeasurement = this.selectedServingWeightMeasurement.name;
          }

          RecipesRepository.updateRecipeServingSize(model)
            .then(() => {
              resolve({body: "Successfully updated recipe serving size"});
              this.loadRecipeDetails();
              this.closeServingSizeModal();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message = error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  removeIngredient(id: number) {
    let ingredient = this.recipesManagementState.recipe.ingredients.find(
      (i: any) => i.ingredient.id === id
    );

    if (ingredient) {
      this.recipesManagementState.recipe.ingredients.splice(
        this.recipesManagementState.recipe.ingredients.indexOf(ingredient),
        1
      );
      this.calculateNutritions();
      this.submit("", null);
    }
  }
  removeSubRecipe(id: number) {
    let subRecipe = this.recipesManagementState.recipe.childRecipes.find(
      (r: any) => r.recipe.id === id
    );

    if (subRecipe) {
      this.recipesManagementState.recipe.childRecipes.splice(
        this.recipesManagementState.recipe.childRecipes.indexOf(subRecipe),
        1
      );
      this.calculateNutritions();
      this.submit("", null);
    }
  }

  // Used to map array of related food groups into shape acceptable for the select component
  transformIntoSelectObject(arr: FoodGroupClassification[]): SelectObj[] {
    return arr.map((el: { id: number; subClass: string }) => {
      return {
        id: el.id,
        name: el.subClass,
      };
    });
  }

  toggleRecipeInst() {
    return (this.showRecipeInst = !this.showRecipeInst);
  }
  toggleScaleRecipeModal() {
    this.showScaleRecipeModal = !this.showScaleRecipeModal;
    if (!this.showScaleRecipeModal && this.scaledRecipeServingSize != null) {
      this.scaledRecipeServingSize = null;
    }
  }
  toggleMoistureLossModal() {
    this.showMoistureLossModal = !this.showMoistureLossModal;

    // If the modal is open make a copy of the recipe to edit
    if (this.showMoistureLossModal) {
      this.recipeCopy = {
        recipeId: this.recipesManagementState.recipe.id,
        ingredients: this.recipesManagementState.recipe.ingredients,
        childRecipes: this.recipesManagementState.recipe.childRecipes,
        cookingMethodId: this.recipesManagementState.recipe.cookingMethodId,
        lossAmount: round(this.recipesManagementState.recipe.lossAmount, 2),
        lossType: this.recipesManagementState.recipe.lossType,
        weight: this.recipesManagementState.recipe.weight,
      };
    }
    else {
      this.setRecipeCookingMethodAndLoss();
    }
  }

  async submitNutrientOverrides({recipeId, changed, removed} : NutrientOverridesParams) {
    try {
      let result = await applyNutrientOverrides(
        recipeId,
        changed,
        removed,
        this.recipesManagementState.recipe.servingQuantityInUnitOfMeasurementOverride,
        this.recipesManagementState.recipe.servingUnitOfMeaurementOverride
      );
      this.recipesManagementState.recipe.nutritionAnalysis = mapNutritions(
        result.data.result.nutritionAnalysis
      );

      this.closeNutrientOverridesModal();
      // Claims may update as a result of overrides. The list should update
      await this.fetchEnabledClaims();
    }
    catch (error) {
      this.$snotify.error(this.translations.get("network-error"));
    }
  }

  closeNutrientOverridesModal(){
    this.showLabelOverrideModal = false;
  }

  openNutrientOverridesModal() {
    this.showLabelOverrideModal = true;
  }

  calculateNutritions() {
    let childRecipeIds: RecipeCalculateSubrecipe[] = [];

    this.recipesManagementState.recipe.childRecipes.map((child: Subrecipe) =>
      childRecipeIds.push({
        recipeId: child.recipe.id,
        quantity: child.quantity ? child.quantity : 1,
        foodGroupClassificationId: child.foodGroupClassificationId != null
          ? child.foodGroupClassificationId
          : 0,
        isAddedSugar: child.isAddedSugar,
        recipeCompositionMeasurementId: child.recipeCompositionMeasurementId,
      })
    );

    let ingredients: RecipeCalculateIngredient[] = [];

    this.recipesManagementState.recipe.ingredients.map((ingr: any) => {
      ingredients.push({
        quantity: ingr.quantity,
        ingredientMeasurementId: ingr.measurement.id,
        isAddedSugar: ingr.isAddedSugar,
        foodGroupClassificationId:
          ingr.foodGroupClassificationId != null
            ? ingr.foodGroupClassificationId
            : 0,
      });
    });
    new Promise((resolve, reject) => {
      // NOTE: We always calculate both rounded and unrounded values, specifying the DV calculation method is unnecessary
      let calcRecipe: RecipeCalculate = {
        recipeId: this.recipesManagementState.recipe.id,
        recipes: childRecipeIds,
        ingredients: ingredients,
        servings: Math.round(this.servingCount * 100) / 100,
        lossAmount:
          this.recipesManagementState.recipe.lossAmount != null
            ? round(this.recipesManagementState.recipe.lossAmount, 2)
            : 0,
        lossType:
          this.recipesManagementState.recipe.lossType != null
            ? this.recipesManagementState.recipe.lossType
            : 0,
        cookingMethodId:
          this.recipesManagementState.recipe.cookingMethodId != null
            ? this.recipesManagementState.recipe.cookingMethodId
            : 0,
        recipeServingType: this.recipeServingType,
        caloriesCalculationMethod: this.calculationMethodModal.selectedCalculationMethod,
        referenceAmount : this.recipesManagementState.recipe.referenceAmount,
        recipeProductType : this.recipesManagementState.recipe.recipeProductType,
        ruleIndex: this.recipeMetadata.roundingRuleIndex
      };
      if (this.recipeServingType === 0) {
        calcRecipe.servingCount = Math.round(this.servingCount * 100) / 100;
      }
      else {
        calcRecipe.servingQuantityInUnitOfMeasurement =
          Math.round(this.servingQuantityInUnitOfMeasurement * 100) / 100;
        calcRecipe.servingUnitOfMeasurement =
          this.selectedServingWeightMeasurement.name;
      }
      RecipesRepository.calculate(calcRecipe)
        .then((response) => {
          this.recipesManagementState.recipe.nutritionAnalysis = mapNutritions(
            response.data.result
          );

          this.servingCount =
            Math.round(response.data.result.servings * 100) / 100;

          this.recipesManagementState.recipe.weight = response.data.result.weight;

          // If serving weight is not a number set it to 0
          if (
            this.recipesManagementState.recipe.nutritionAnalysis != null &&
            isNaN(
              this.recipesManagementState.recipe.nutritionAnalysis.weightPerServing
            )
          ) {
            this.recipesManagementState.recipe.nutritionAnalysis.weightPerServing = 0;
          }

          if (
            this.recipesManagementState.recipe
              .servingQuantityInUnitOfMeasurement == null
          ) {
            this.servingQuantityInUnitOfMeasurement =
              response.data.result.weightPerServing;
          }

          // If serving weight is not a number set it to 0
          if (isNaN(this.servingQuantityInUnitOfMeasurement)) {
            this.servingQuantityInUnitOfMeasurement = 0;
          }

          resolve({});
        })
        .catch((error) => {
          let message = "";
          if (error.response) {
            message = error.response.data.errorMessages[0].userVisibleMessage;
          }
          else {
            message = this.translations.get("network_error");
          }
          reject({ body: message });
        });
    });
  }

  resetServingSizeModal() {
    this.recipeServingType = this.recipesManagementState.recipe.recipeServingType;

    // Define value for serving count in case serving weight is chosen, set servings as recommendation for the user
    this.servingCount = (
      this.recipesManagementState.recipe.servingCount != null
        ? Math.round(this.recipesManagementState.recipe.servingCount * 100) / 100
        : Math.round(this.recipesManagementState.recipe.servings * 100) / 100
    );

    // Define value for serving weight in case serving count is chosen,
    // set weightPerServing as recommendation for the user
    this.servingQuantityInUnitOfMeasurement = (
      this.recipesManagementState.recipe.servingQuantityInUnitOfMeasurement != null
        ? Math.round(this.recipesManagementState.recipe.servingQuantityInUnitOfMeasurement * 100) / 100
        : Math.round(this.recipesManagementState.recipe.weightPerServing * 100) / 100
    );

    if (this.recipesManagementState.recipe.servingQuantityInUnitOfMeasurement != null) {
      this.getSelectedServingWeight(
        this.recipesManagementState.recipe.servingUnitOfMeasurement
      );
    }

    this.recipeServingType = this.recipesManagementState.recipe.recipeServingType;
    this.closeServingSizeModal();
  }

  resetState() {
    // If trial upsell is open above Export Label, close only the upsell.
    if (this.showTrialUpsellModal) {
      this.closeTrialUpsellModal();
      return;
    }

    this.showCategoryModal = false;
    this.showIngredientModal = false;
    this.showExportLabelModal = false;
    this.closeExportLabelDemo();
    this.ingredientSearchString = "";
    this.resetIngredientSearchResults();
    this.showServingSizeModal = false;
    this.showAddIngredientModal = false;
    this.showTrialUserModal = false;
    // (<any>window).zE("webWidget", "close");
    // Refetch allergen data for the current recipe when closing ExportLabel
    if (
      !this.skipAllergenReloadOnReset
      && this.recipesManagementState
      && this.recipesManagementState.recipe
      && this.recipesManagementState.recipe.id
    ) {
      this.loadAllergenConfiguration(this.recipesManagementState.recipe.id);
    }
  }

  resetIngredientSearchResults() {
    this.searchResults = {
      customIngredients: [],
      ingredients: [],
      recipes: [],
      dbIngredients: [],
    };
  }

  openExportLabelDemo() {
    this.isExportLabelDemoLoading = true;
    this.showExportLabelDemoModal = true;
  }

  closeExportLabelDemo() {
    this.showExportLabelDemoModal = false;
    this.isExportLabelDemoLoading = false;
  }

  handleExportLabelDemoLoad() {
    this.isExportLabelDemoLoading = false;
  }

  toggleServingModal() {
    this.showServingSizeModal = !this.showServingSizeModal;
    if (this.showServingSizeModal) {
      this.checkForLiquidVolumes();
    }
    this.servingQuantityInUnitOfMeasurement =
      Math.round(this.servingQuantityInUnitOfMeasurement * 100) / 100;
  }

  closeServingSizeModal() {
    this.showServingSizeModal = false;
  }

  /* Method that disables given buttons until the user saves the changes he made to the recipe */
  disableButtonsBeforeSave() {
    this.recipeSaved = false;
  }

  calculateRecipesCostRedirect(redirectLink: string, redirectParams: any = null) {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    this.$router.push({
      name: redirectLink,
      params: redirectParams,
    });
  };

  async fetchEnabledClaims() {
    // Sanity check
    if (this.countryId != null) {
      const response = await ClaimsRepository.getEnabledClaimsForRecipe(
        this.recipesManagementState.recipe.id,
        this.countryId
      );

      if (response && response.data) {
        this.enabledClaims = response.data;
      }
    }
  }

  submit(redirectLink: string = "", redirectParams: any = null) {
    let childRecipeIds: {
      quantity: number;
      recipeId: number;
      recipeCompositionMeasurementId: number;
      isAddedSugar: boolean;
    }[] = [];

    this.recipesManagementState.recipe.childRecipes.map((child: any) => {
      childRecipeIds.push({
        recipeId: child.recipe.id,
        recipeCompositionMeasurementId: child.recipeCompositionMeasurementId,
        quantity: child.quantity ? child.quantity : 1,
        isAddedSugar: child.isAddedSugar,
      });
    });


    let ingredients: {
      quantity: number;
      ingredientMeasurementId: number;
      isAddedSugar: boolean;
    }[] = [];

    this.recipesManagementState.recipe.ingredients.map((ingr: any) => {
      ingredients.push({
        quantity: ingr.quantity,
        ingredientMeasurementId: ingr.measurement.id,
        isAddedSugar: ingr.isAddedSugar,
      });
    });

    this.$validator.validateAll().then(async(result) => {
      if (result && this.recipesManagementState.recipe.recipeCategory.id) {
        if (this.$route.name === "recipesEdit") {
          if (ingredients) {
            this.updateRecipe(
              childRecipeIds,
              ingredients,
              redirectLink,
              redirectParams
            );
          }
        }
        else if (this.$route.name === "recipesAdd") {
          if (ingredients) {
            this.saveNewRecipe(
              childRecipeIds,
              ingredients,
              redirectLink,
              redirectParams
            );
          }
        }
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  updateRecipe(
    childRecipeIds: any,
    ingredients: any,
    redirectLink: string,
    redirectParams: any = null
  ) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_toastr_saving"),
      () =>
        new Promise((resolve, reject) => {
          let servingCount = null;
          let servingQuantityInUnitOfMeasurement = null;

          if (this.recipeServingType == 0) {
            // Serving count
            servingCount = Math.round(this.servingCount * 100) / 100;
          }
          else {
            // Serving weight
            servingQuantityInUnitOfMeasurement =
              Math.round(
                this.recipesManagementState.recipe
                  .servingQuantityInUnitOfMeasurement * 100
              ) / 100;
          }

          const productType = this.recipesManagementState.recipe.recipeProductType
            ? this.recipesManagementState.recipe.recipeProductType
            : createDefaultProductType();

          let updateRecipe: UpdateRecipe = {
            recipes: childRecipeIds,
            ingredients: ingredients,
            description: this.recipesManagementState.recipe.description,
            recipeCategoryId: this.recipesManagementState.recipe.recipeCategory
              ? this.recipesManagementState.recipe.recipeCategory.id
              : 0,
            name: this.recipesManagementState.recipe.name,
            recipeId: this.recipesManagementState.recipe.id,
            instructions: this.recipesManagementState.recipe.instructions,
            recipeServingType:
              this.recipeServingType !=
              this.recipesManagementState.recipe.recipeServingType
                ? this.recipeServingType
                : this.recipesManagementState.recipe.recipeServingType,
            servingCount: servingCount,
            servingQuantityInUnitOfMeasurement:
              servingQuantityInUnitOfMeasurement,
            servingUnitOfMeasurement:
              this.recipesManagementState.recipe.servingUnitOfMeasurement,
            adjustedAddedSugarsValue:
              this.recipesManagementState.recipe.adjustedAddedSugarsValue,
            nutritionCountry: this.countryId,
            referenceAmount: this.recipesManagementState.recipe.referenceAmount,
            productTypeId: productType.id,
            nutrientTypesToCalculate: this.recipesManagementState.recipe.nutritionCountry
              ? this.recipeMetadata.isSupplementLabel ? null : getPreviewNutrientsForCountry(
                this.recipesManagementState.recipe.nutritionCountry.id)
              : null,
          };

          RecipesRepository.updateRecipe(updateRecipe)
            .then(async(response) => {
              await this.saveAllergenConfiguration(updateRecipe.recipeId);
              this.skipAllergenReloadOnReset = false;

              this.recipeSaved = true;

              this.mapRecipeDetails(response.data.result);
              await this.ensureRecipeNutritionCountryLoaded(response.data.result.nutritionCountry);
              await this.hydrateRecipeStatusesFromSavedAnalysis(response.data.result.id);
              this.loadRecipeComponents();
              if (redirectLink !== "") {
                if (redirectParams != null) {
                  this.$router.push({
                    name: redirectLink,
                    params: redirectParams,
                  });
                }
                else {
                  this.$router.push({
                    name: redirectLink,
                  });
                }
              }
              resolve({
                body: this.translations.get("rec_toastr_updated"),
              });
            })
            .catch((error) => {
              this.skipAllergenReloadOnReset = false;
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 2000,
        closeOnClick: true,
      }
    );
  }

  saveNewRecipe(
    childRecipeIds: any,
    ingredients: any,
    redirectLink: string,
    redirectParams: any = null
  ) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_toastr_saving"),
      () =>
        new Promise((resolve, reject) => {
          const productType = this.recipesManagementState.recipe.recipeProductType
            ? this.recipesManagementState.recipe.recipeProductType
            : createDefaultProductType();

          let createRecipe: CreateRecipe = {
            recipes: childRecipeIds,
            ingredients: ingredients,
            description: this.recipesManagementState.recipe.description,
            recipeCategoryId: this.recipesManagementState.recipe.recipeCategory
              ? this.recipesManagementState.recipe.recipeCategory.id
              : 0,
            name: this.recipesManagementState.recipe.name,
            instructions: this.recipesManagementState.recipe.instructions,
            servingCount: Math.round(this.servingCount * 100) / 100,
            adjustedAddedSugarsValue: this.recipesManagementState.recipe.adjustedAddedSugarsValue,
            isCombinedRecipe: false,
            nutritionCountry: this.countryId,
            referenceAmount: this.recipesManagementState.recipe.referenceAmount,
            productTypeId: productType.id
          };

          RecipesRepository.createRecipe(createRecipe)
            .then(async(response) => {
              await this.saveAllergenConfiguration(response.data.id);
              this.skipAllergenReloadOnReset = false;
              await this.markTrialActivityCompleted(
                TrialRestaurantActivity.CreatedFirstRecipe
              );

              this.$router.push({
                name: "recipesEdit",
                params: { id: response.data.id.toString() },
              });

              this.mapRecipeDetails(response.data);
              this.loadRecipeComponents();

              if (redirectLink !== "") {
                this.$router.push({
                  name: redirectLink,
                  params: redirectParams,
                });
              }
              resolve({
                body: this.translations.get("rec_toastr_saved"),
              });
            })
            .catch((error: ErrorResponse) => {
              this.skipAllergenReloadOnReset = false;
              let message;
              if (error.response) {
                message = error.response.data.message;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 2000,
        closeOnClick: true,
      }
    );
  }

  // Show recipe cost button, check if user paid for recipe cost
  showRecipeCostBtn() {
    if (this.accountState.userDetails.addOns) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.RecipeCost);
    }
    return false;
  }

  chatStatus() {
    let w = <any>window;
    if (w && w.zendeskChatStatus) {
      return w.zendeskChatStatus;
    }
    return "offline";
  }

  showZendesk() {
    if ((<any>window).zE) {
      if (this.chatStatus() == "online") {
        (<any>window).zE("webWidget", "show");
        (<any>window).zE("webWidget", "open");
      }
    }
  }

  getRecipeEditHref(recipeId: number) {
    return this.$router.resolve({
      name: "recipesEdit",
      params: { id: recipeId.toString() },
    }).href;
  }

  /** HOOKS **/
  mounted() {
    Vue.nextTick(() => {
      setTimeout(() => {
        (<any>window).$.Pages.init();
      }, 2000);
    });
    document.getElementsByTagName("body")[0].classList.add("recipe-management");
  }

  beforeDestroy() {
    document
      .getElementsByTagName("body")[0]
      .classList.remove("recipe-management");
  }

  created() {
    this.fetchSubrecipeServingSizes();
    this.fetchNutritionCountries();

    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: [
        "common",
        "recipes",
        "nutrition-labels",
        "ingredients",
        "recipe costing",
        "cautionary"
      ],
    })
      .then(() => {
        if (this.$route.name === "recipesEdit") {
          document.title =
            this.translations.get("rec_title_edit") +
            " " +
            this.translations.get("meta_title_postfix");
          this.loadRecipeDetails();
        }
        else {
          // New recipes are not saved by default
          this.recipeSaved = false;
          document.title =
            this.translations.get("recipesAdd") +
            " " +
            this.translations.get("meta_title_postfix");
          this.fetchPackageTypes();
        }
      });

    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "validationrules",
      collections: ["recipes"],
    });

    if (this.$route.name === "recipesEdit") {
      this.checkForLiquidVolumes();
    }
    else {
      this.fetchCategories();
    }


    this.preloadNutrientSearchSources();
    this.loadCookingMethods();
    this.loadFoodGroupClasses();
    this.fetchCategories();
  }

  preloadNutrientSearchSources() {
    SubscriptionRepository.listCurrentSubscriptionNutrientSources().then(
      (response) => {
        this.nutrientSources = response.data.result;

        // select all except USDA branded nutrient source for searching, as requested in support 13762
        this.nutrientsListForSearch = this.nutrientSources.filter(
          (nutrient) => nutrient != 3
        );
      }
    );
  }

  loadCookingMethods() {
    RecipesRepository.listCookingMethods().then((response) => {
      this.cookingMethods = [
        { id: -1, name: "Not specified" },
        ...response.data.result,
      ];
      if (response.data.result && response.data.result.length) {
        if (
          this.recipesManagementState.recipe &&
          this.recipesManagementState.recipe.cookingMethodId
        ) {
          let cookingMethod = this.cookingMethods.find(
            (method: any) =>
              method.id === this.recipesManagementState.recipe.cookingMethodId
          );
          if (cookingMethod) this.selectedCookingMethod = { ...cookingMethod };
        }
        this.selectedCookingMethod = this.cookingMethods[0];
      }
    });
  }

  loadFoodGroupClasses() {
    RecipesRepository.listFoodGroupClasses().then((response) => {
      // Get subclasses for every class
      const foodGroupClasses = ["None Applicable", ...response.data.result];
      if (foodGroupClasses != null && foodGroupClasses.length) {
        this.foodGroupClasses = foodGroupClasses.map(
          (name: string, index: number) => {
            return {
              id: index,
              name: name,
            };
          }
        );
      }
    });
  }
}
export default RecipesManagement;
